import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Fragment, useId, useRef, useState, useSyncExternalStore } from 'react';
import { useRowFocusKeeper } from "./row-focus.js";
import { isolate, otherLang } from "./i18n.js";
import { FIELD_FORMATS, westernDigits, appliesTo, heldBack, inspect, isRowAction, pathFor, refOf, refTab, rowsOf, runnable, isMediaRef } from "./manifest.js";
import { ALL, CLOSED, COLUMNS_NARROW, COLUMNS_WIDE, columnPlan, resolveLabel, valueLabel } from "./derive.js";
import { TONES, emphasisOf, marksOf } from "./manifest.js";
import { Badge, Button, Checkbox, Chevron, FOCUS, IconButton, InlineLink, Input, Lookup, Menu, Segmented, Select, TONE_CLASS, TONE_FILL, Textarea, cx } from "./ui.js";
import { AudioInput, DateInput, FileInput, NumberInput, TimeInput, dateDisplay, formatBytes, formatHijriDay, useDateDisplay, wallClock } from "./fields.js";
import { CheckGrid, hueOf, initialsOf } from "./blocks.js";
import { PRIMARY_HOTKEY, ariaKeyshortcuts } from "./hotkeys.js";
import { LinesField } from "./lines-field.js";
export { formatHijriDay, formatBytes } from "./fields.js";
/** How a name that is a link looks: the text's own colour at medium weight, underlined on hover and focus — never blue. */
export const NAME_LINK = 'font-medium text-foreground underline-offset-4 decoration-foreground/40 hover:underline focus-visible:underline';
/**
 * A row's own name when it opens the record's page: drawn in the brand colour so it reads as a way
 * in. In the plain ink of NAME_LINK an invoice number looked like text, and the owner could not find
 * the finance record pages that were there all along.
 */
export const OPEN_LINK = 'font-medium text-brand underline-offset-4 decoration-brand/40 hover:underline focus-visible:underline';
/**
 * A record's name: a link to its page where the viewer may open it, plain text where not — and,
 * when asked for (`openLabel`), a small quiet mark right beside it that opens the same page. The
 * owner could not tell a name that opens a record from one that does not (2026-09-26): the mark
 * says so, in every list, card, record page, hub feed and panel alike, because each of them draws
 * a named record through this one component.
 *
 * The mark is its own link, next to the name's and never inside it — anchors do not nest — with
 * the same address and handler, for the pointer only: out of the tab order and the reader's tree,
 * since the name is the same link (P08-12). Its words stay on its tooltip. Muted, in
 * the brand colour on hover and focus. In a row (a table's `tr`, a card, each `data-row`) a mouse
 * sees it on the row's hover or focus only, so a long list is not a column of arrows; a touch
 * screen, which has no hover, always sees it; anywhere else it is always there.
 */
export function RecordLink({ href, children, openLabel, rtl, tone = 'name', className, onClick, ...rest }) {
    // A name in the other script says its language (P08-10); the page's script is told by its direction.
    const lang = typeof children === 'string' && rtl !== undefined ? otherLang(children, rtl ? 'ar' : 'en') : undefined;
    const spoken = lang ? { lang } : {};
    if (!href)
        return _jsx("span", { dir: "auto", "data-record-link": "", ...spoken, ...rest, className: className, children: children });
    const name = (_jsx("a", { href: href, ...spoken, ...rest, dir: "auto", "data-record-link": href, ...(onClick ? { onClick } : {}), className: cx(tone === 'open' ? OPEN_LINK : NAME_LINK, FOCUS, 'rounded-sm', className), children: children }));
    if (!openLabel)
        return name;
    return (
    // `min-w-0`, so inside a box that truncates (a board card's title) the name can shrink to its
    // ellipsis instead of running past the card over its neighbour.
    _jsxs("span", { "data-record-ref": "", className: "inline-flex min-w-0 max-w-full items-center gap-1", children: [name, _jsx("a", { href: href, "aria-label": openLabel, title: openLabel, "data-record-open": "", tabIndex: -1, "aria-hidden": "true", ...(onClick ? { onClick } : {}), className: cx('inline-flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-[color,opacity]', 'hover:text-brand focus-visible:text-brand pointer-fine:in-[[data-row]]:opacity-0 group-hover/row:opacity-100 group-focus-within/row:opacity-100 focus-visible:opacity-100', FOCUS), children: _jsx(OpenRecordMark, { rtl: Boolean(rtl) }) })] }));
}
/** Lucide's ArrowUpRight, and in a right-to-left page its mirror ArrowUpLeft. Drawn here: this package carries no icon dependency. */
function OpenRecordMark({ rtl }) {
    return (_jsx("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", "data-mark": rtl ? 'arrow-up-left' : 'arrow-up-right', children: rtl ? _jsxs(_Fragment, { children: [_jsx("path", { d: "M7 17V7h10" }), _jsx("path", { d: "M17 17 7 7" })] }) : _jsxs(_Fragment, { children: [_jsx("path", { d: "M7 7h10v10" }), _jsx("path", { d: "M7 17 17 7" })] }) }));
}
/** The words of the mark beside a record's name: «افتح السجل: {name}», or «افتح السجل» when the name is not words. */
export function openRecordLabel(i18n, name) {
    const said = name.trim();
    return said ? i18n.t('record.open.named', { name: said }) : i18n.t('record.open');
}
/** All but the last four characters of an identifier, hidden. */
export function maskLast4(value) {
    const text = String(value ?? '');
    if (!text)
        return '—';
    return `•••• ${text.slice(-4)}`;
}
/**
 * Every failure looks the same wherever it happens, and it always says which of the four it is.
 * A spinner that never stops and an empty table are the two ways a screen lies about this.
 *
 * Each state gets its own colour and its own mark, because "not reachable", "not allowed" and
 * "took too long" call for three different next moves and one grey box suggests none of them.
 */
const STATE_TONE = {
    unavailable: { edge: 'border-s-state-unavailable', ink: 'text-state-unavailable', disc: 'bg-state-unavailable/12' },
    forbidden: { edge: 'border-s-state-forbidden', ink: 'text-state-forbidden', disc: 'bg-state-forbidden/12' },
    timeout: { edge: 'border-s-state-timeout', ink: 'text-state-timeout', disc: 'bg-state-timeout/12' },
    unsupported: { edge: 'border-s-state-unavailable', ink: 'text-state-unavailable', disc: 'bg-state-unavailable/12' },
    planned: { edge: 'border-s-border', ink: 'text-muted-foreground', disc: 'bg-muted' },
    loading: { edge: 'border-s-border', ink: 'text-muted-foreground', disc: 'bg-muted' },
    empty: { edge: 'border-s-border', ink: 'text-muted-foreground', disc: 'bg-muted' },
};
/** The marks. Drawn here rather than imported: this package carries no icon dependency. */
function StateMark({ state, className }) {
    const common = { width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true, className };
    switch (state) {
        case 'forbidden':
            return _jsxs("svg", { ...common, children: [_jsx("circle", { cx: "8", cy: "8", r: "6" }), _jsx("path", { d: "M3.8 3.8l8.4 8.4" })] });
        case 'timeout':
            return _jsxs("svg", { ...common, children: [_jsx("circle", { cx: "8", cy: "8", r: "6" }), _jsx("path", { d: "M8 4.8V8l2.2 1.4" })] });
        case 'loading':
            return _jsx("svg", { ...common, className: `${className ?? ''} motion-safe:animate-spin`, children: _jsx("path", { d: "M14 8a6 6 0 1 1-2.2-4.6" }) });
        case 'empty':
            return _jsxs("svg", { ...common, children: [_jsx("path", { d: "M2.5 5.5h11v7a1 1 0 0 1-1 1h-9a1 1 0 0 1-1-1z" }), _jsx("path", { d: "M2.5 5.5 4 2.5h8l1.5 3" })] });
        case 'planned':
            return _jsxs("svg", { ...common, children: [_jsx("path", { d: "M8 2.5v3M13.5 8h-3M8 13.5v-3M2.5 8h3" }), _jsx("circle", { cx: "8", cy: "8", r: "1.2" })] });
        default:
            return _jsxs("svg", { ...common, children: [_jsx("path", { d: "M8 2.5 14.5 13.5h-13z" }), _jsx("path", { d: "M8 6.5v3M8 11.5h.01" })] });
    }
}
export function StateNotice({ state, detail, said, i18n, onRetry }) {
    const tone = STATE_TONE[state] ?? STATE_TONE['unavailable'];
    if (said?.key) {
        const words = i18n.t(said.key, said.params);
        if (words !== said.key)
            detail = words;
    }
    return (_jsxs("div", { className: `flex items-start gap-3 rounded-xl border border-s-[3px] bg-card p-5 text-sm ${tone.edge}`, "data-state": state, role: state === 'loading' ? 'status' : 'note', children: [_jsx("span", { className: `flex size-8 shrink-0 items-center justify-center rounded-full ${tone.disc} ${tone.ink}`, children: _jsx(StateMark, { state: state }) }), _jsxs("div", { className: "grid gap-1", children: [_jsx("p", { className: "font-semibold text-foreground", children: i18n.t(`state.${state}`) }), detail ? _jsx("p", { dir: "auto", className: "text-[13px] leading-relaxed text-muted-foreground", children: detail }) : null, onRetry && state !== 'loading' ? (_jsx("button", { type: "button", onClick: onRetry, className: "mt-2 inline-flex h-9 w-fit items-center rounded-md border px-4 text-sm font-medium transition-colors hover:bg-accent focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring", children: i18n.t('action.retry') })) : null] })] }));
}
/**
 * The shape of what is coming, while it comes. A spinner says "something is happening"; this says
 * what, so the page does not jump when the rows arrive and take a different amount of room.
 */
export function Skeleton({ rows = 3, cards = 2, i18n }) {
    return (_jsx("div", { role: "status", "aria-label": i18n.t('state.loading'), className: "grid gap-4", children: Array.from({ length: cards }, (_, c) => (_jsxs("div", { className: "overflow-hidden rounded-xl border bg-card", children: [_jsx("div", { className: "border-b px-5 py-4", children: _jsx("span", { className: "block h-4 w-40 rounded bg-muted motion-safe:animate-pulse" }) }), Array.from({ length: Math.max(1, rows - c) }, (_, i) => (_jsxs("div", { className: `flex items-center justify-between gap-6 px-5 py-4 ${i > 0 ? 'border-t' : ''}`, children: [_jsxs("span", { className: "grid gap-2", children: [_jsx("span", { className: "block h-3.5 w-32 rounded bg-muted motion-safe:animate-pulse" }), _jsx("span", { className: "block h-3 w-52 rounded bg-muted/70 motion-safe:animate-pulse" })] }), _jsx("span", { className: "block h-8 w-40 rounded-md bg-muted motion-safe:animate-pulse" })] }, i)))] }, c))) }));
}
/** What a manifest asked for and this version cannot draw. Shown, never worked around. */
export function ManifestRefusal({ problems, i18n }) {
    return (_jsxs("div", { className: "rounded-xl border border-s-[3px] border-s-state-unavailable bg-card p-5 text-sm", "data-state": "unsupported", role: "note", children: [_jsx("p", { className: "text-foreground", children: i18n.t('screen.unsupported') }), _jsxs("details", { className: "mt-2 text-xs text-muted-foreground", children: [_jsx("summary", { className: "cursor-pointer", children: i18n.t('screen.unsupported.details') }), _jsx("ul", { className: "mt-1 list-disc space-y-1 ps-5", dir: "ltr", children: problems.map((p, i) => _jsx("li", { "data-code": p.code, children: p.detail }, i)) })] })] }));
}
/**
 * Arabic has two numeral systems and a browser will pick the Eastern one (٤٢) for `ar`. An input
 * types Western digits whatever the locale, so following the default would put ٤٢ in the table
 * and 42 in the field that edits it. One system, chosen here, and it is the one Arabic business
 * software uses.
 */
export function numbersIn(locale) {
    return locale === 'ar' ? 'ar-u-nu-latn' : locale;
}
/**
 * A calendar day, written the reader's way.
 *
 * The value arrives as YYYY-MM-DD (or an ISO instant, whose day is taken) and is read as a *local*
 * day: `new Date("2026-09-22")` is midnight UTC, and in a timezone behind UTC that prints as the
 * 21st. The parts are therefore handed to the Date constructor separately, which is the difference
 * between a contract that starts on the 22nd and one that appears to start the day before.
 *
 * A value that is not a date is returned untouched: an invalid date shown as itself gets noticed
 * and corrected, while an invented one — today, or the epoch — is believed.
 */
export function formatDay(value, locale) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!m)
        return value;
    const day = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (Number.isNaN(day.getTime()))
        return value;
    return day.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
}
/**
 * The day (`YYYY-MM-DD`) of a moment on the workspace's clock, as `formatCell` draws it for a
 * `datetime` — so a moment's Hijri twin names the day the cell shows. Null for what is not a moment.
 */
export function localDayOf(value) {
    if (typeof value !== 'string' || !value)
        return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime()))
        return null;
    const w = wallClock(d);
    return `${w.year}-${String(w.month).padStart(2, '0')}-${String(w.day).padStart(2, '0')}`;
}
/**
 * The options that draw a moment on the workspace's clock: its zone (the calendar's setting; the
 * reader's until it is known) and the 12-hour hour with its marker — «9:30 ص», «9:30 AM» — wherever a
 * person reads an hour (owner, 2026-10-14; it was the 24-hour hour until the Arabic content review
 * of 2026-10-27 found it). The hour is `numeric` («7:30», not «07:30»). Never the seconds. The 24-hour
 * `HH:MM` lives only where a machine reads it: a field's value, wallClock.
 */
export function momentOptions(extra = {}) {
    const zone = dateDisplay().timeZone;
    const clock = extra.hour || extra.timeStyle;
    return {
        ...extra,
        ...(clock ? { hourCycle: 'h12', ...(extra.hour ? { hour: 'numeric' } : {}) } : {}),
        ...(zone ? { timeZone: zone } : {}),
    };
}
/** A moment in full — «20 مارس 2026، 09:30» — on the workspace's clock, to the minute. */
export function formatInstant(value, locale) {
    const at = new Date(value);
    return Number.isNaN(at.getTime()) ? value
        : at.toLocaleString(numbersIn(locale), momentOptions({ day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }));
}
/**
 * Two days as one span, the month and year said once when they are shared — «12–16 أكتوبر»,
 * «28 سبتمبر – 3 أكتوبر 2027» — and the year left out when it is this one. Not days: as they came.
 */
export function formatDayRange(from, to, locale, now = new Date()) {
    const a = /^(\d{4})-(\d{2})-(\d{2})/.exec(from);
    const b = /^(\d{4})-(\d{2})-(\d{2})/.exec(to);
    if (!a || !b)
        return `${from}–${to}`;
    const d1 = new Date(Number(a[1]), Number(a[2]) - 1, Number(a[3]));
    const d2 = new Date(Number(b[1]), Number(b[2]) - 1, Number(b[3]));
    const thisYear = d1.getFullYear() === now.getFullYear() && d2.getFullYear() === now.getFullYear();
    const withYear = (d, month = true) => d.toLocaleDateString(locale, { day: 'numeric', ...(month ? { month: 'long' } : {}), ...(thisYear ? {} : { year: 'numeric' }) });
    if (a[1] === b[1] && a[2] === b[2]) {
        if (a[3] === b[3])
            return withYear(d1);
        // «12–16 أكتوبر»: the second day carries the month (and year); the first is its number.
        return `${Number(a[3]).toLocaleString(locale)}–${withYear(d2)}`;
    }
    return `${d1.toLocaleDateString(locale, { day: 'numeric', month: 'long', ...(a[1] === b[1] ? {} : thisYear ? {} : { year: 'numeric' }) })} – ${withYear(d2)}`;
}
/**
 * A time of day with no date on it — a shift starts at 08:00 on every day it runs. Drawn on the
 * 12-hour clock with its marker («8:00 ص», «8:00 AM») and Western digits, from an `HH:MM` or `HH:MM:SS` the server sent;
 * anything else is shown as it came, so a bad value is seen and fixed rather than silently
 * turned into midnight.
 */
export function formatClock(value, locale) {
    const m = /^(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value);
    if (!m)
        return value;
    const h = Number(m[1]), min = Number(m[2]);
    if (h > 23 || min > 59)
        return value;
    const t = new Date(2000, 0, 1, h, min);
    return t.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', hourCycle: 'h12' });
}
/**
 * A phone number grouped the way it is read aloud — «+966 50 222 3344» — from however it was
 * stored. Only the spacing changes, and only for the shapes it knows (a three-digit calling code
 * and nine digits, a one-digit code and ten, a local ten starting 0); anything else is shown as
 * written, because a number regrouped wrongly is a wrong number.
 */
export function formatPhone(value) {
    const d = value.replace(/[\s().-]/g, '');
    let m = /^(?:\+|00)(\d{3})(\d{2})(\d{3})(\d{4})$/.exec(d);
    if (m)
        return `+${m[1]} ${m[2]} ${m[3]} ${m[4]}`;
    m = /^(?:\+|00)(\d)(\d{3})(\d{3})(\d{4})$/.exec(d);
    if (m)
        return `+${m[1]} ${m[2]} ${m[3]} ${m[4]}`;
    m = /^(0\d{2})(\d{3})(\d{4})$/.exec(d);
    if (m)
        return `${m[1]} ${m[2]} ${m[3]}`;
    return value;
}
/**
 * The address a phone number is dialled at — `tel:+966502223344` — or null when the value is not a
 * number one could dial: the digits alone, a leading `+` (or `00`) kept as the international prefix,
 * seven to fifteen digits (E.164's longest). Tapped on a phone it starts the call (decision 20,
 * tap-to-call); on a computer it opens whatever the reader set up to call with.
 */
export function telHref(value) {
    const d = value.replace(/[\s().\-\u200e\u200f\u2066-\u2069]/g, '');
    const m = /^(?:\+|00)?(\d{7,15})$/.exec(d);
    if (!m)
        return null;
    return `tel:${/^(?:\+|00)/.test(d) ? '+' : ''}${m[1]}`;
}
/**
 * The address a WhatsApp chat with the number opens at — `https://wa.me/966502223344` — or null.
 * The digits are telHref's; wa.me has no local form, so only a number written with its international
 * prefix (`+` or `00`) is given one: «0502223344» would open a chat with nobody.
 */
export function whatsappHref(value) {
    const tel = telHref(value);
    return tel?.startsWith('tel:+') ? `https://wa.me/${tel.slice(5)}` : null;
}
/**
 * The address that writes to an email address — `mailto:sara@example.com` — or null when the value
 * is not one address. Nothing that would add a subject, a body or a second recipient to the link
 * (`?`, `&`, `,`, `;`, a space) is let through: the link writes to the address shown, and only to it.
 */
export function mailtoHref(value) {
    const v = value.trim();
    return /^[^\s@<>()[\]\\,;:"?&#/%]+@[^\s@<>()[\]\\,;:"?&#/%]+\.[^\s@<>()[\]\\,;:"?&#/%.]+$/.test(v) && v.length <= 254 ? `mailto:${v}` : null;
}
/**
 * A moment as a person says it on the day: «اليوم 11:00», «غدًا 09:30», «أمس 14:00»; another day of
 * this year by its day and month — «8 سبتمبر» — and of another year in full. The day is the
 * reader's local one, like `formatDay`'s.
 */
export function formatMoment(value, i18n, now = new Date(), withTime = false) {
    // A bare day («2026-10-07») is that day where the reader is, with no hour: read as UTC midnight it
    // was drawn «اليوم 03:00» at +3, an hour nobody set.
    const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    const at = day ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3])) : new Date(value);
    if (Number.isNaN(at.getTime()))
        return value;
    const locale = numbersIn(i18n.locale);
    // A moment's day is the workspace's (its zone, once the calendar says it); a bare day is itself.
    const dayOf = (d, own) => { const w = own ? wallClock(d, null) : wallClock(d); return Date.UTC(w.year, w.month - 1, w.day); };
    const days = Math.round((dayOf(at, !!day) - dayOf(now, false)) / 86_400_000);
    if (day && days >= -1 && days <= 1)
        return i18n.t(days === 0 ? 'value.moment.today_day' : days === 1 ? 'value.moment.tomorrow_day' : 'value.moment.yesterday_day');
    const time = at.toLocaleTimeString(locale, momentOptions({ hour: '2-digit', minute: '2-digit' }));
    if (days === 0)
        return i18n.t('value.moment.today', { time });
    if (days === 1)
        return i18n.t('value.moment.tomorrow', { time });
    if (days === -1)
        return i18n.t('value.moment.yesterday', { time });
    const opts = day ? (o) => o : momentOptions;
    const dayWords = new Date(dayOf(at, !!day)).getUTCFullYear() === new Date(dayOf(now, false)).getUTCFullYear()
        ? at.toLocaleDateString(locale, opts({ month: 'long', day: 'numeric' }))
        : at.toLocaleDateString(locale, opts({ year: 'numeric', month: 'long', day: 'numeric' }));
    return withTime && !day ? `${dayWords} ${time}` : dayWords;
}
/**
 * Words around a row's values, `{column}` each: a moment «20 أغسطس 14:02», a word by `valuesKey`.
 * `{k:money}` and `{k:number}` read a figure the route sends as a decimal string («2,475.00 · 15%»);
 * `{k:moment}` a day as the reader says it («20 سبتمبر»), `{k:at}` with its hour, `{k:month}` its
 * month («سبتمبر 2026»). A name the row does not carry stays as written.
 */
export function templateWords(text, row, i18n, valuesKey) {
    const locale = numbersIn(i18n.locale);
    // `{k:money|number|moment|at|month}` read a value as that; `{k:<unit>}` writes a count in the unit's
    // plural («5 أيام»); `{from~to}` says a span of days once («12–16 أكتوبر»), or its one end when the
    // other is empty («من 1 يوليو»); a bare ISO day is a day.
    return text.replace(/\{([A-Za-z_][A-Za-z0-9_]*)(?:~([A-Za-z_][A-Za-z0-9_]*))?(?::([a-z][a-z_]*))?\}/g, (whole, k, to, as) => {
        const v = row[k];
        if (v === undefined)
            return whole;
        // A span with only one end says that end in words: «من 1 يوليو» with no end yet, «حتى 31 أكتوبر» with no start.
        if (to && row[to] !== undefined && (v === null) !== (row[to] === null)) {
            const end = v === null ? row[to] : v;
            return typeof end === 'string' && /^\d{4}-\d{2}-\d{2}/.test(end)
                ? i18n.t(v === null ? 'value.span.until' : 'value.span.from', { day: formatDay(end, locale) }) : whole;
        }
        if (v === null)
            return '—';
        if (to)
            return typeof v === 'string' && typeof row[to] === 'string' ? formatDayRange(v, String(row[to]), locale) : whole;
        // `{k:signed}` a change as money with its sign, kept left to right inside words: «⁦+18,240.00⁩ عن سبتمبر».
        if (as === 'signed') {
            const n = Number(v);
            if (v === '' || typeof v === 'boolean' || !Number.isFinite(n))
                return whole;
            return n === 0 ? formatMoney(0, undefined, locale) : `\u2066${n > 0 ? '+' : '\u2212'}${formatMoney(Math.abs(n), undefined, locale)}\u2069`;
        }
        if (as && !['money', 'number', 'moment', 'at', 'month'].includes(as)) {
            return v !== '' && typeof v !== 'boolean' && Number.isFinite(Number(v)) ? unitWords(as, Number(v), i18n) : whole;
        }
        if (as === 'moment' && typeof v === 'string')
            return formatMoment(v, i18n);
        if (as === 'at' && typeof v === 'string')
            return formatMoment(v, i18n, new Date(), true);
        if (as === 'month' && typeof v === 'string' && !Number.isNaN(Date.parse(v)))
            return new Date(v).toLocaleDateString(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' });
        if ((as === 'money' || as === 'number') && Number.isFinite(Number(v)) && v !== '' && typeof v !== 'boolean') {
            return as === 'money' ? formatMoney(Number(v), undefined, locale) : Number(v).toLocaleString(locale);
        }
        if (typeof v === 'number')
            return v.toLocaleString(locale);
        if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v))
            return formatMoment(v, i18n, new Date(), true);
        if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v))
            return formatDay(v, locale);
        // A code or a name in the other script keeps its own order inside the sentence (AR-19).
        return isolate(valueLabel({ key: k, label: k, ...(valuesKey ? { values_key: valuesKey } : {}) }, v, i18n), i18n.dir);
    });
}
/**
 * A lookup's words for each of its rows (`options_from.label_template`, or `label_template_key` in
 * the unit's catalogue), written by `templateWords` in the reader's language; null for a row the
 * template cannot fill, which keeps its `label`. Undefined when the field declares no template.
 */
export function optionWords(from, i18n) {
    if (!from?.label_template && !from?.label_template_key)
        return undefined;
    const template = resolveLabel(from.label_template, from.label_template_key, i18n);
    if (!template)
        return undefined;
    return (row) => {
        const text = templateWords(template, row, i18n).trim();
        return text && !/\{[A-Za-z_]/.test(text) ? text : null;
    };
}
/** A count in its unit, in the reader's plural forms: «4 سنوات», «سنة واحدة», «11 سنة». */
/**
 * How long ago a moment was, in the words a waiting figure uses: hours under a day, days after it,
 * and «—» when there is none — nothing waiting is not «0 days».
 */
export function sinceWords(since, i18n, now = Date.now()) {
    const at = typeof since === 'string' ? Date.parse(since) : NaN;
    if (!Number.isFinite(at))
        return '—';
    const hours = Math.max(0, Math.floor((now - at) / 3_600_000));
    if (hours < 24)
        return unitWords('hours', Math.max(1, hours), i18n);
    return unitWords('days', Math.floor(hours / 24), i18n);
}
export function unitWords(unit, n, i18n) {
    const figure = n.toLocaleString(numbersIn(i18n.locale));
    let form = 'other';
    try {
        form = new Intl.PluralRules(i18n.locale).select(n);
    }
    catch { /* an unknown locale counts as "other" */ }
    const own = `unit.${unit}.${form}`;
    const words = i18n.t(own, { n: figure });
    if (words !== own)
        return words;
    const other = `unit.${unit}.other`;
    const fallback = i18n.t(other, { n: figure });
    return fallback === other ? figure : fallback;
}
/**
 * An event's name as a reader says it, never its key. The catalogue's own words first
 * (`activity.event.leave.request.approved`); otherwise built from what happened to what — the
 * thing (`perm.thing.*`) or the module (`nav.module.*`) and the verb as a noun (`event.verb.*`):
 * «الإجازات: طلب» for `leave.requested`. The key only when neither part has a word.
 */
export function eventName(name, i18n) {
    const bare = name.replace(/@\d+$/, '');
    const word = (key) => { const w = i18n.t(key); return w === key ? null : w; };
    const own = word(`activity.event.${bare}`);
    if (own)
        return own;
    const parts = bare.split('.');
    if (parts.length < 2)
        return name;
    const verb = parts[parts.length - 1];
    const module = parts[0];
    const thing = parts.slice(1, -1).join('.');
    const what = (thing ? word(`perm.thing.${module}.${thing}`) ?? word(`perm.thing.${thing}`) : null) ?? word(`nav.module.${module}`);
    const doing = word(`event.verb.${verb}`);
    return what && doing ? i18n.t('event.named', { what, doing }) : name;
}
/** A country's name for its ISO 3166-1 code, in the reader's language, from the runtime's own tables. A code it cannot name: as it came. */
export function countryName(code, locale) {
    if (!/^[A-Z]{2}$/.test(code))
        return code;
    try {
        return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? code;
    }
    catch {
        return code;
    }
}
/** The value as an image address a page may show, or null: base64 PNG, JPEG, WebP or SVG only. */
export function imageSource(value) {
    if (typeof value !== 'string')
        return null;
    return /^data:image\/(png|jpeg|webp|svg\+xml);base64,[A-Za-z0-9+/]+={0,2}$/.test(value) ? value : null;
}
/** The messages of a transcript, as the `conversation` display draws them: anything else is left out. */
export function conversationLines(value) {
    if (!Array.isArray(value))
        return [];
    return value.flatMap((m) => {
        if (!m || typeof m !== 'object')
            return [];
        const { role, text, at } = m;
        if ((role !== 'user' && role !== 'assistant') || typeof text !== 'string' || !text.trim())
            return [];
        return [{ role, text, ...(typeof at === 'string' && !Number.isNaN(Date.parse(at)) ? { at } : {}) }];
    });
}
/**
 * A number to its step: `toGrain(135.49, 0.5, 'down')` is 135, `toGrain(-0.3, 0.5, 'down')` −0.5,
 * `toGrain(0.375, 0.01)` 0.38. The step's own decimals bound the answer, so 0.1 × 3 is 0.3 and not
 * 0.30000000000000004; a hair of tolerance keeps a value already on a step there.
 */
export function toGrain(n, grain, round = 'nearest') {
    if (!Number.isFinite(n) || !(grain > 0))
        return n;
    const steps = n / grain;
    const k = round === 'down' ? Math.floor(steps + 1e-9) : round === 'up' ? Math.ceil(steps - 1e-9) : Math.round(steps + (steps >= 0 ? 1e-9 : -1e-9));
    const decimals = (String(grain).split('.')[1] ?? '').length;
    const out = Number((k * grain).toFixed(Math.min(decimals, 20)));
    return Object.is(out, -0) ? 0 : out;
}
export function formatCell(value, column, i18n) {
    if (value === null || value === undefined)
        return '—';
    // A message the server words by key — `{key, params}`: a payslip line's reason — said in the
    // reader's words with its values; a key with no words is shown as the key, never as JSON.
    if (isMessage(value))
        return i18n.t(value.key, value.params);
    // Masked wherever it is written — a cell, a file carried away, a line read aloud.
    if (column.mask === 'last4')
        return maskLast4(value);
    if (column.zero === 'dash' && Number(value) === 0 && value !== '' && typeof value !== 'boolean')
        return '—';
    // A change, with its sign, kept left to right inside Arabic words: «⁦+12⁩ منذ أكتوبر».
    if (column.signed && Number.isFinite(Number(value)) && value !== '' && typeof value !== 'boolean' && Number(value) !== 0) {
        const n = Number(value);
        return `⁦${n > 0 ? '+' : '−'}${formatCell(Math.abs(n), { ...column, signed: false }, i18n)}⁩`;
    }
    if (column.display === 'phone')
        return formatPhone(String(value));
    if (column.display === 'moment')
        return formatMoment(String(value), i18n);
    // A picture has no words: a file or a line read aloud says only that there is one.
    if (column.display === 'image')
        return imageSource(value) ? i18n.t('cell.image') : '—';
    // A transcript: a file or a line read aloud says how many messages, not their words.
    if (column.display === 'conversation') {
        const n = conversationLines(value).length;
        return n ? unitWords('messages', n, i18n) : '—';
    }
    if (column.display === 'country')
        return countryName(String(value), i18n.locale);
    if (column.display === 'event')
        return eventName(String(value), i18n);
    // A number shown to its step (`grain`), the value itself untouched: 135.49 days held reads 135 (HRM-23).
    if (column.grain && column.grain > 0 && value !== '' && typeof value !== 'boolean' && Number.isFinite(Number(value))) {
        const stepped = toGrain(Number(value), column.grain, column.round);
        if (stepped !== Number(value))
            return formatCell(stepped, { ...column, grain: undefined }, i18n);
    }
    if ((column.unit || typeof column.of === 'number') && Number.isFinite(Number(value)) && typeof value !== 'boolean') {
        const n = Number(value);
        const figure = column.unit ? unitWords(column.unit, n, i18n) : n.toLocaleString(numbersIn(i18n.locale));
        return typeof column.of === 'number' ? `${figure} / ${column.of.toLocaleString(numbersIn(i18n.locale))}` : figure;
    }
    switch (column.type) {
        // A moment to the minute: the day in words and the hour, never the seconds (HR review, attendance).
        // On the workspace's clock and the 12-hour hour with its marker (owner, 2026-10-14).
        case 'datetime': return formatInstant(String(value), i18n.locale);
        // A day, not a moment. `datetime` would draw 00:00 beside it and claim a precision the row
        // never carried; a plain string would lose the reader's own month names.
        case 'date': return formatDay(String(value), numbersIn(i18n.locale));
        // A clock, not a day: the hours a shift keeps, drawn without inventing a date to hang them on.
        case 'time': return formatClock(String(value), numbersIn(i18n.locale));
        case 'number': return Number(value).toLocaleString(numbersIn(i18n.locale));
        case 'money': return formatMoney(Number(value), column.currency, numbersIn(i18n.locale));
        // Already in percent: 12.5 is 12.5%, at most two decimals, kept left to right inside Arabic words.
        case 'percent': return Number.isFinite(Number(value)) ? `${Number(value).toLocaleString(numbersIn(i18n.locale), { maximumFractionDigits: 2 })}%` : String(value);
        case 'bytes': return formatBytes(Number(value), numbersIn(i18n.locale));
        case 'count': return String(Array.isArray(value) ? value.length : value);
        // Several strings in one cell. Written out rather than counted, because the reader of a
        // roles column wants to know which roles, and JSON.stringify would have said ["owner"].
        // A column that names its words (`values_key`) has each plain item read from the catalogue: a
        // party's roles read «عميل، مورّد», not customer, vendor. Without it, as the item arrived.
        case 'list': return Array.isArray(value) ? (value.length ? value.map((v) => (column.values_key && (v === null || typeof v !== 'object') ? valueLabel(column, v, i18n) : namedLabel(v, i18n, column.values_key))).join(i18n.t('value.separator')) : '—') : String(value);
        // Namespaced keys, summarised by the thing that owns them. A role holding thirty permissions
        // is a number nobody can act on; the same role shown as the modules it reaches is a sentence.
        case 'namespaces': {
            if (!Array.isArray(value))
                return String(value);
            if (!value.length)
                return '—';
            // The wildcard is not a module with one key in it; it is every permission there is and every
            // one there will be. Grouped like a name it would read "* 1", which says the opposite.
            if (value.some((k) => String(k) === '*'))
                return i18n.t('value.everything');
            return namespaces(value).map(([n, c]) => `${n} ${c.toLocaleString(numbersIn(i18n.locale))}`).join(i18n.t('value.separator'));
        }
        // "true" is not a word in the reader's language, and a table is read, not parsed.
        case 'boolean': {
            // A column that has words for its two values uses them here too. Without this the same
            // value read one way in a filter's options and another in the cell beside it: `core` was
            // offering "Core / Optional" to filter by and printing "Yes" on the card.
            const named = i18n.t(`value.${column.key}.${String(Boolean(value))}`);
            return named === `value.${column.key}.${String(Boolean(value))}` ? i18n.t(value ? 'value.yes' : 'value.no') : named;
        }
        // A column type this version does not format is shown as it arrived rather than dropped: a
        // cell is a statement of fact, and text is a faithful way to state one. Catalogue keys
        // `value.{column}.{value}` win when they exist, so account_type.asset_cash reads as Cash.
        default:
            if (typeof value === 'object')
                return JSON.stringify(value);
            return valueLabel(column, value, i18n);
    }
}
/** A value shaped `{key, params}`: a catalogue key and the values its words name. */
export function isMessage(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return false;
    const v = value;
    return typeof v.key === 'string' && /^[a-z][a-z0-9_.]*$/.test(v.key) && 'params' in v
        && (v.params === null || v.params === undefined || (typeof v.params === 'object' && !Array.isArray(v.params)));
}
/**
 * Two decimal places, Western digits, and a currency code only when the column named one.
 * Intl places the code for the writing direction; an unknown code falls back to the bare amount
 * rather than inventing a symbol for a deployment nobody declared.
 */
export function formatMoney(n, currency, locale) {
    if (!Number.isFinite(n))
        return '—';
    if (currency) {
        try {
            return new Intl.NumberFormat(locale, {
                style: 'currency',
                currency,
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(n);
        }
        catch {
            // ISO 4217 is closed; a typo must not crash the screen.
        }
    }
    return n.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
/** Dotted keys grouped by their first segment, in a stable order: {a.b, a.c, z.d} → [[a,2],[z,1]]. */
export function namespaces(keys) {
    const counts = new Map();
    for (const key of keys) {
        const text = String(key);
        const at = text.indexOf('.');
        const owner = at > 0 ? text.slice(0, at) : text;
        counts.set(owner, (counts.get(owner) ?? 0) + 1);
    }
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
}
/**
 * Each tone as the three classes that draw a pill in it: the ink, the ground at a tenth of it, and
 * an edge between.
 *
 * Written out rather than composed from the tone's name, because Tailwind reads this file as text
 * to decide which classes to emit. `bg-tone-${tone}/10` built at run time would be a class nobody
 * generated, and the badge would arrive with no colour at all — visible only to whoever happened to
 * look at that value.
 */
/**
 * The small label, in both scripts.
 *
 * It used to be uppercase and tracked, which is a Latin device: Arabic has no capital letters, and
 * the theme switches tracking off for it on purpose because it pulls the joins apart. So in the
 * language this product is written for, the entire label system did nothing at all. Weight and
 * colour say the same thing, and they say it in both scripts.
 */
const LABEL = 'text-[13px] font-semibold text-muted-foreground';
// Wraps under a reader's own text spacing (1.4.12) instead of running into the next label.
const SMALL_LABEL = 'text-xs font-semibold text-muted-foreground break-words';
/**
 * The surface an action editor is drawn on: a panel, and nothing inside it.
 *
 * It used to dress the unit's own markup as well — every `input`, `button`, `label`, `table` under
 * it, by element — from when an editor shipped bare elements and this string was their only
 * appearance. Editors are drawn with the SDK's components now (a unit may not write a raw control
 * at all, and infra/checks/design-system.sh says so), and those descendant rules outranked the
 * components' own classes: every button under an editor, the primary one included, was painted
 * the background colour — white text on white — and every input was forced to 44px whatever size
 * it asked for. The components carry their own appearance; this carries only the panel.
 */
const SLOT_PANEL = 'grid min-w-0 gap-5 rounded-xl border bg-card p-5 text-[15px]';
/**
 * What a named thing is called, for this reader. A string is its own name; an object carries
 * `name` and, where somebody wrote one, `name_ar` — the Arabic reader gets the Arabic one. Under a
 * catalogue prefix (`wordsUnder`), an object with a `key` is the catalogue's word for that key first:
 * a module as `nav.module.{key}`, whatever language the server named it in.
 */
export function namedLabel(item, i18n, wordsUnder) {
    if (item === null || item === undefined)
        return '—';
    if (typeof item !== 'object')
        return String(item);
    const named = item;
    if (wordsUnder && typeof named.key === 'string' && named.key) {
        const own = `${wordsUnder}.${named.key}`;
        const word = i18n.t(own);
        if (word !== own)
            return word;
    }
    const arabic = i18n.locale === 'ar' && typeof named.name_ar === 'string' && named.name_ar ? named.name_ar : null;
    return String(arabic ?? named.name ?? named.key ?? '—');
}
/**
 * What a card calls one of its rows: the row's own kind when the panel names the column holding it
 * (`panel.label_from` — a document type on the approval engine's inbox, worded as its cell is),
 * else the panel's label, else the screen's title.
 */
export function panelRowLabel(manifest, row, i18n) {
    const panel = manifest?.panel;
    const from = panel?.label_from;
    const own = from && row ? row[from] : undefined;
    if (own !== null && own !== undefined && own !== '') {
        const column = manifest?.columns?.find((c) => c.key === from) ?? { key: from, label: '' };
        return formatCell(readersValue(own, column, row, i18n), column, i18n);
    }
    return panel?.label || panel?.label_key ? resolveLabel(panel.label, panel.label_key, i18n) : resolveLabel(manifest?.title, manifest?.title_key, i18n);
}
/**
 * What a cell says in the reader's language, before it is formatted. A column with `values_by` is
 * the catalogue's word for that column of the row (a unit by its key: «الموارد البشرية»); a row that
 * also carries `<column>_ar` is read in Arabic on an Arabic screen (a role's «المدير» beside its
 * «Administrator»), the same rule the options of a field follow. Otherwise the value as it came.
 */
export function readersValue(value, column, row, i18n) {
    if (!row)
        return value;
    if (column.values_by && column.values_key) {
        const by = row[column.values_by];
        if (by !== null && by !== undefined && by !== '') {
            const own = `${column.values_key}.${String(by)}`;
            const word = i18n.t(own);
            if (word !== own)
                return word;
        }
    }
    // A row that names the value by catalogue key beside it (`metric_name_key` with `metric_name`, or
    // the property `value_key_from` names) is read in the catalogue's word: the service's own text is
    // English. Only a dotted key the catalogue knows; anything else leaves the value as it came.
    const keyed = row[column.value_key_from ?? `${column.key}_key`];
    if (typeof keyed === 'string' && keyed.includes('.')) {
        const word = i18n.t(keyed);
        if (word !== keyed)
            return word;
    }
    // Several keys — a condition on two options of a choice — are said each in its word, joined; only
    // when the catalogue has every one of them, or the value stays as it came, whole.
    if (Array.isArray(keyed) && keyed.length && keyed.every((k) => typeof k === 'string' && k.includes('.'))) {
        const words = keyed.map((k) => i18n.t(k));
        if (words.every((w, i) => w !== keyed[i]))
            return words.join(i18n.t('value.separator'));
    }
    if (i18n.locale === 'ar' && typeof value === 'string') {
        const twin = row[`${column.key}_ar`];
        if (typeof twin === 'string' && twin.trim())
            return twin;
    }
    return value;
}
/** The mark a manifest asked for, if the chrome answers that name. Nothing at all if it does not. */
export function Mark({ name, ctx, className }) {
    const draw = name ? ctx.icons?.[name] : undefined;
    return draw ? draw({ className: className ?? 'size-4' }) : null;
}
/**
 * The round mark at the head of a card: a value's initials over a colour derived from the value.
 *
 * The one place a hash may choose a colour. Everywhere else colour means a state and is declared,
 * because a hash that picks green for `archived` is a machine asserting something nobody said.
 * Here it distinguishes one row from another and classifies nothing, so two roles sharing a hue
 * says only that two strings landed on one number.
 */
/**
 * One cell, drawn.
 *
 * formatCell answers what a value says; this answers how it is shown, which is a second question
 * with its own vocabulary — a badge, a person, a key in a monospaced face, a colour as a colour.
 * Anything the manifest did not ask for is the text, which is what every cell was before this.
 */
export function Cell({ value: given, column, ctx, row }) {
    // A cell whose own value is a `{module}.{entity}:{id}` token (`ref.token`) says the id, never the token.
    const tokenTarget = column.ref?.token === column.key ? refOf(column, row, given) : null;
    const value = tokenTarget ? tokenTarget.id : readersValue(given, column, row, ctx.i18n);
    const plain = _jsx(CellBody, { value: value, column: column, ctx: ctx, ...(row ? { row } : {}) });
    // A yes/no flag said on the value as a small badge after it — «إلزامي» on an item's name (L-13).
    const marks = marksOf(column, row);
    const body = marks.length
        ? (_jsxs("span", { className: "inline-flex flex-wrap items-center gap-1.5", "data-marks": "", children: [plain, marks.map((m, i) => (_jsx(Badge, { tone: (TONES.includes(m.tone) ? m.tone : 'neutral'), dot: false, "data-mark": "", children: resolveLabel(m.label, m.label_key, ctx.i18n) }, i)))] }))
        : plain;
    // A value's quiet second line, when the manifest names one: an asset's «AC-10232 · منذ 7 يوليو» under its name.
    const under = column.second && row && column.display !== 'person' && row[column.second] !== null && row[column.second] !== undefined && row[column.second] !== ''
        ? _jsx(CellBody, { value: row[column.second], column: { key: column.second, label: '', ...(column.second_values_key ? { values_key: column.second_values_key } : {}), ...(column.second_template ? { template: column.second_template, ...(column.second_template_key ? { template_key: column.second_template_key } : {}) } : {}) }, ctx: ctx, row: row })
        : null;
    const drawn = under
        ? _jsxs("span", { className: "grid gap-0.5 leading-tight", "data-second": column.second, children: [_jsx("span", { children: body }), _jsx("span", { className: "text-[12.5px] font-normal text-muted-foreground", children: under })] })
        : body;
    const ref = column.ref;
    if (!ref)
        return drawn;
    // Another record, named. Its id is the row's `id` column or this value; a viewer who may open its
    // page gets a link there, anybody else the same words as plain text.
    // The row may name a record of another kind than the column's own (`also`): a document's owner.
    const target = tokenTarget ?? refOf(column, row, value);
    if (!target)
        return drawn;
    const id = target.id;
    const known = !ref.label_from && (value === null || value === undefined || String(value) === id) ? ctx.referenceLabel?.(target.entity, id) : undefined;
    // The owner of the id says which of its pages it opens: an invoicing document that is a vendor bill.
    const tab = refTab(column, target);
    const href = ctx.recordLink?.(known?.entity ?? target.entity, id, ...(tab ? [tab] : [])) ?? null;
    const tag = { 'data-ref': target.entity, 'data-ref-id': id };
    // A person stays a person — the disc, the name, the number under it — and only the name is the
    // link, with the mark beside it: the whole block as one link read as a button, and the disc of a
    // name the owner resolved was lost with it.
    if (column.display === 'person' && !ref.label_from) {
        const named = known ? known.label : value;
        const shown = known && column.detail && row && (row[column.detail] === null || row[column.detail] === undefined) && known.number
            ? { ...row, [column.detail]: known.number } : row;
        return _jsx(CellBody, { value: named, column: column, ctx: ctx, ...(shown ? { row: shown } : {}), link: { href, tag } });
    }
    const words = ref.label_from && row ? _jsx(CellBody, { value: row[ref.label_from], column: { key: ref.label_from, label: '' }, ctx: ctx, row: row })
        : known ? known.label : drawn;
    // A name its owner resolved keeps its quiet line under it — who prepared a run, and when — with only the name the link.
    if (known && under && !ref.label_from) {
        const said = known.label;
        return (_jsxs("span", { className: "grid gap-0.5 leading-tight", "data-second": column.second, children: [_jsx("span", { children: href ? _jsx(RecordLink, { href: href, ...tag, openLabel: openRecordLabel(ctx.i18n, said), rtl: ctx.i18n.dir === 'rtl', children: said }) : _jsx("span", { ...tag, children: said }) }), _jsx("span", { className: "text-[12.5px] font-normal text-muted-foreground", children: under })] }));
    }
    const said = ref.label_from && row ? formatCell(row[ref.label_from], { key: ref.label_from, label: '' }, ctx.i18n)
        : known ? known.label : formatCell(value, column, ctx.i18n);
    return href
        ? _jsx(RecordLink, { href: href, ...tag, openLabel: openRecordLabel(ctx.i18n, said), rtl: ctx.i18n.dir === 'rtl', children: words })
        : _jsx("span", { ...tag, children: words });
}
/** What a cell says in words, for a label: a person's name (or, absent, the line under it), otherwise the value as written. */
export function cellWords(row, column, ctx) {
    const value = readersValue(row[column.key], column, row, ctx.i18n);
    if (column.display === 'person' && (value === null || value === undefined || value === '') && column.detail) {
        const detail = row[column.detail];
        return detail === null || detail === undefined ? '' : String(detail);
    }
    return value === null || value === undefined ? '' : formatCell(value, column, ctx.i18n);
}
function CellBody({ value, column, ctx, row, link }) {
    const dates = useDateDisplay();
    if (column.mask === 'last4' && value !== null && value !== undefined && value !== '') {
        return _jsx("span", { dir: "ltr", "data-masked": "", className: "tabular-nums", children: maskLast4(value) });
    }
    const text = formatCell(value, column, ctx.i18n);
    // A day, and the workspace reads its days in both calendars (CP-CAL-004): the Hijri twin beside
    // the Gregorian date, or on its `title` only. Western digits in both. The machine-readable day
    // is on <time>, so neither drawing is the only record of what the value was.
    // A statutory day (`hijri` on the column) says its Hijri twin on a line of its own whatever the setting.
    if (column.type === 'date' && column.hijri && value !== null && value !== undefined && value !== '') {
        const iso = String(value).slice(0, 10);
        const hijri = formatHijriDay(iso, dates.reference, ctx.i18n.locale);
        if (hijri && text !== String(value)) {
            return (_jsxs("span", { "data-dual-date": "", className: "inline-grid gap-0.5 leading-tight", children: [_jsx("time", { dateTime: iso, className: "whitespace-nowrap", children: text }), _jsx("span", { dir: "auto", "data-hijri": "", className: "whitespace-nowrap text-[12px] font-normal text-muted-foreground", children: hijri })] }));
        }
    }
    if (column.type === 'date' && dates.hijri !== 'off' && value !== null && value !== undefined && value !== '') {
        const iso = String(value).slice(0, 10);
        const hijri = formatHijriDay(iso, dates.reference, ctx.i18n.locale);
        if (hijri && text !== String(value)) {
            return dates.hijri === 'inline'
                ? (_jsxs("span", { "data-dual-date": "", className: "inline-flex flex-wrap items-baseline gap-x-2", children: [_jsx("time", { dateTime: iso, children: text }), _jsx("span", { dir: "auto", "data-hijri": "", className: "text-[12.5px] text-muted-foreground", children: hijri })] }))
                // The tooltip is a mouse's: a reader hears the Hijri day too, after the Gregorian one.
                : _jsxs("time", { dateTime: iso, title: hijri, children: [text, _jsx("span", { className: "sr-only", children: ` (${hijri})` })] });
        }
    }
    // A moment carries the Hijri day too (#24): the day of the moment as the cell draws it — the
    // reader's local day, never the UTC day an ISO string starts with, or a shift ending at 01:00
    // in Riyadh would be named by yesterday's Hijri date.
    if (column.type === 'datetime' && dates.hijri !== 'off' && value !== null && value !== undefined && value !== '') {
        const day = localDayOf(value);
        const hijri = day ? formatHijriDay(day, dates.reference, ctx.i18n.locale) : null;
        if (hijri) {
            return dates.hijri === 'inline'
                ? (_jsxs("span", { "data-dual-date": "", className: "inline-flex flex-wrap items-baseline gap-x-2", children: [_jsx("time", { dateTime: String(value), children: text }), _jsx("span", { dir: "auto", "data-hijri": "", className: "text-[12.5px] text-muted-foreground", children: hijri })] }))
                : _jsxs("time", { dateTime: String(value), title: hijri, children: [text, _jsx("span", { className: "sr-only", children: ` (${hijri})` })] });
        }
    }
    // `person` is asked before the absent-value shortcut below, because absent is the case it is
    // there for: most people have no name written for them, and a cell reading "—" over an address
    // says less than the address by itself. Every other display wants the shortcut — a badge with
    // no value is a coloured pill saying nothing.
    if (column.display === 'person') {
        const detail = column.detail && row ? row[column.detail] : undefined;
        // The line under the name in its own words where it is a code with words — «انضمّ» for `joined`
        // (`value.{detail}.{value}`) — and as it came otherwise: a number, an item's name.
        const second = detail === undefined || detail === null ? null
            : typeof detail === 'string' && column.detail ? valueLabel({ key: column.detail, label: '', ...(column.second_values_key ? { values_key: column.second_values_key } : {}) }, detail, ctx.i18n) : String(detail);
        const first = (value === null || value === undefined) && second ? second : text;
        // Nobody: no name and no line under it — a department with no manager, an item nobody holds.
        // One dash, as every other empty cell says it, and no disc: a disc of «—» beside a second «—»
        // drew somebody where there is nobody (HR review, 2026-10-07).
        if (!second && (value === null || value === undefined || value === '')) {
            return _jsx("span", { "data-person-none": "", className: "text-muted-foreground", children: "\u2014" });
        }
        // The disc is the design's "who" cell: 30px of initials the eye lands on before it reads.
        // Hashed from the text, so two people are told apart and nobody is classified by it.
        const who = first || second || '';
        const langOf = (words) => { const lang = otherLang(words, ctx.i18n.locale); return lang ? { lang } : {}; };
        return (_jsxs("span", { className: "flex items-center gap-[9px]", children: [who ? (_jsx("span", { "aria-hidden": "true", className: `flex size-[30px] shrink-0 items-center justify-center rounded-full text-[13px] font-bold ${TONE_CLASS[hueOf(who)]}`, children: initialsOf(who) })) : null, _jsxs("span", { className: "grid gap-0.5 leading-tight", children: [link?.href
                            ? _jsx("span", { className: "font-semibold text-foreground", children: _jsx(RecordLink, { href: link.href, ...link.tag, openLabel: openRecordLabel(ctx.i18n, first), rtl: ctx.i18n.dir === 'rtl', children: first }) })
                            : link ? _jsx("span", { dir: "auto", className: "font-semibold text-foreground", ...langOf(first), ...link.tag, children: first })
                                : _jsx("span", { dir: "auto", className: "font-semibold text-foreground", ...langOf(first), children: first }), second && second !== first ? (_jsx("span", { dir: "auto", className: "text-[13px] tabular-nums text-muted-foreground", ...langOf(second), children: second })) : null] })] }));
    }
    if (value === null || value === undefined)
        return text;
    // Two of the row's values in one cell, in words: «مشغل رافعة شوكية ×2», «14 · 3 في المقابلة».
    if (column.template && row) {
        const words = templateWords(resolveLabel(column.template, column.template_key, ctx.i18n), row, ctx.i18n, column.values_key);
        const lang = otherLang(words, ctx.i18n.locale);
        return _jsx("span", { dir: "auto", ...(lang ? { lang } : {}), children: words });
    }
    // A count in the unit its row names — a criterion's «3 سنوات» beside an age that has none.
    if (column.unit_from && row && typeof row[column.unit_from] === 'string' && row[column.unit_from] && value !== '' && Number.isFinite(Number(value))) {
        return unitWords(String(row[column.unit_from]), Number(value), ctx.i18n);
    }
    // Out of what the row says: points «25 / 30» against that criterion's own weight.
    if (typeof column.of === 'string' && row) {
        const of = row[column.of];
        const whole = of === null || of === undefined ? text : `${text} / ${formatCell(of, { key: column.of, label: '', type: 'number' }, ctx.i18n)}`;
        return _jsx("span", { dir: "ltr", className: "whitespace-nowrap tabular-nums", children: whole });
    }
    if (typeof column.of === 'number')
        return _jsx("span", { dir: "ltr", className: "whitespace-nowrap tabular-nums", children: text });
    // A phone number is a link that calls it — tap-to-call — written as it is read aloud; a click on it
    // calls and does not also open the row it sits on.
    // A column that says the number is also on WhatsApp (`channels: ["whatsapp"]`) adds a second, smaller
    // link beside it that opens a chat in a new tab; a number with no international prefix gets none.
    if (column.display === 'phone') {
        const tel = telHref(String(value));
        if (!tel)
            return _jsx("span", { dir: "ltr", className: "whitespace-nowrap tabular-nums", children: text });
        const wa = column.channels?.includes('whatsapp') ? whatsappHref(String(value)) : null;
        const stop = (e) => e.stopPropagation();
        return (_jsxs("span", { dir: "ltr", className: "whitespace-nowrap tabular-nums", children: [_jsx(InlineLink, { href: tel, "data-tel": "", onClick: stop, children: text }), wa ? _jsxs(_Fragment, { children: [" ", _jsx(InlineLink, { href: wa, target: "_blank", "data-whatsapp": "", className: "text-[12px] font-medium", onClick: stop, "aria-label": ctx.i18n.t('cell.whatsapp.open', { number: text }), children: ctx.i18n.t('cell.whatsapp') })] }) : null] }));
    }
    // An email address is a link that writes to it; a click on it does not also open the row.
    if (column.display === 'email') {
        const mail = mailtoHref(String(value));
        return mail
            ? _jsx("span", { dir: "ltr", className: "break-all", children: _jsx(InlineLink, { href: mail, "data-mailto": "", onClick: (e) => e.stopPropagation(), children: text }) })
            : _jsx("span", { dir: "ltr", className: "break-all", children: text });
    }
    if (column.display === 'moment')
        return _jsx("time", { dateTime: String(value), dir: "auto", className: "whitespace-nowrap", children: text });
    // A count in its unit is one word to a reader — «14 يوماً» does not break between the two.
    if (column.unit && !column.display && !column.highlight && value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value))) {
        return _jsx("span", { className: "whitespace-nowrap", children: text });
    }
    // A figure that matters past a line — a vacant seat — is a pill; below it, the plain number. The
    // pill is only seen, so a reader hears «needs action» after the figure: without it, finding the
    // three refused rows among forty meant listening to all forty (HR review, 2026-10-27).
    if (column.highlight && typeof value !== 'object' && Number(value) > column.highlight.above) {
        const tone = (TONES.includes(column.highlight.tone) ? column.highlight.tone : 'neutral');
        return _jsxs(Badge, { tone: tone, dot: false, "data-highlight": "", className: "tabular-nums", children: [text, _jsx("span", { className: "sr-only", children: ctx.i18n.t('cell.highlight') })] });
    }
    // A picture the row carries as an image address — an invoice's QR code. Only a base64 image is
    // drawn, in an <img>, which runs nothing; anything else is not a picture and is said as absent.
    if (column.display === 'image') {
        const src = imageSource(value);
        return src
            ? _jsx("img", { src: src, alt: resolveLabel(column.label, column.label_key, ctx.i18n), "data-image": column.key, className: "size-36 rounded-sm border p-1" })
            : '—';
    }
    // A transcript a row carries — a report's conversation with the assistant — as a simple chat, read
    // only: each message with who said it, its words as text (never markup, never a link), in a box
    // that scrolls on its own when the conversation is long.
    if (column.display === 'conversation') {
        const lines = conversationLines(value);
        if (!lines.length)
            return '—';
        return (_jsx("ol", { className: "m-0 grid max-h-96 list-none gap-2 overflow-y-auto p-0 text-[13px] font-normal", "data-conversation": column.key, children: lines.map((l, i) => (_jsxs("li", { "data-conversation-role": l.role, className: l.role === 'user' ? 'grid max-w-[88%] justify-self-start gap-0.5 rounded-lg bg-muted px-3 py-2' : 'grid max-w-[94%] justify-self-end gap-0.5 rounded-lg border px-3 py-2', children: [_jsxs("span", { className: "text-[11.5px] font-semibold text-muted-foreground", dir: "auto", children: [ctx.i18n.t(`cell.conversation.${l.role}`), l.at ? _jsxs(_Fragment, { children: [" \u00B7 ", _jsx("time", { dateTime: l.at, children: formatMoment(l.at, ctx.i18n, new Date(), true) })] }) : null] }), _jsx("span", { className: "whitespace-pre-wrap break-words leading-6 text-foreground", dir: "auto", children: l.text })] }, i))) }));
    }
    switch (column.display) {
        case 'badge': {
            if (column.untoned === 'plain' && !column.tones?.[String(value)])
                return valueLabel(column, value, ctx.i18n);
            const tone = (column.tones?.[String(value)] ?? 'neutral');
            // The dot carries the tone as a shape as well as a colour, and it is what lets the word
            // itself stay at a size that can be read. Badge draws it.
            return _jsx(Badge, { tone: TONES.includes(tone) ? tone : 'neutral', children: valueLabel(column, value, ctx.i18n) });
        }
        // Named things, each in its own colour: a member's roles. The name is the reader's — Arabic when
        // one was written and the screen is Arabic — and the colour is the one somebody chose for it.
        case 'badges': {
            const items = Array.isArray(value) ? value : [value];
            if (!items.length)
                return '—';
            return (_jsx("span", { className: "inline-flex flex-wrap gap-1", "data-badges": true, children: items.map((item, i) => {
                    const colour = item && typeof item === 'object' ? String(item.colour ?? '') : '';
                    const tone = (TONES.includes(colour) ? colour : 'neutral');
                    return _jsx(Badge, { tone: tone, dot: false, children: namedLabel(item, ctx.i18n) }, i);
                }) }));
        }
        // Handled above, because it is the one display that has something to say about an absent value.
        // A code is one word: «L-2024-0001» never breaks at its hyphens, and its cell is as wide as it.
        case 'mono': return _jsx("span", { dir: "ltr", className: "whitespace-nowrap font-mono text-xs", children: text });
        case 'progress': {
            // The number is always written: it is what the export carries and what a screen reader
            // announces. The bar is the part that makes two rows comparable at a glance, and it is
            // hidden from assistive technology so the value is not announced twice.
            const raw = Number(value);
            const pct = Number.isFinite(raw) ? raw : 0;
            const filled = Math.max(0, Math.min(100, pct));
            const band = pct >= 90 ? 'high' : pct >= 60 ? 'mid' : 'low';
            const tone = (column.tones?.[band] ?? 'neutral');
            const label = `${pct.toLocaleString(numbersIn(ctx.i18n.locale))}%`;
            return (_jsxs("span", { role: "meter", "aria-label": resolveLabel(column.label, column.label_key, ctx.i18n), "aria-valuenow": pct, "aria-valuemin": 0, "aria-valuemax": 100, "aria-valuetext": label, "data-tone": tone, className: "inline-flex items-center gap-2", children: [_jsx("span", { "aria-hidden": true, className: "h-1.5 w-16 overflow-hidden rounded-sm bg-muted", children: _jsx("span", { className: `block h-full rounded-sm ${TONE_FILL[tone] ?? TONE_FILL.neutral}`, style: { inlineSize: `${filled}%` } }) }), _jsx("span", { dir: "ltr", className: "text-xs tabular-nums", children: label })] }));
        }
        case 'relative': return _jsx("span", { dir: "auto", title: text, children: text });
        default: break;
    }
    // A colour is the one value that can be shown as itself. The text stays beside it: a swatch
    // alone cannot be read out, compared, or copied.
    if (column.type === 'colour' && /^#[0-9a-fA-F]{3,8}$/.test(String(value))) {
        return (_jsxs("span", { className: "inline-flex items-center gap-2", children: [_jsx("span", { "aria-hidden": "true", "data-swatch": String(value), style: { backgroundColor: String(value) }, className: "size-3.5 shrink-0 rounded-full border" }), _jsx("span", { dir: "ltr", className: "font-mono text-xs", children: text })] }));
    }
    // An amount never breaks between its digits: on a phone a column too narrow for it gets its
    // natural width (naturalWidth) instead of a figure split across two lines.
    if (column.type === 'money' || column.type === 'percent') {
        return _jsx("span", { dir: "ltr", className: "whitespace-nowrap tabular-nums", children: text });
    }
    // Words in the other script say their language, so a reader voices them in it (P08-10).
    const lang = otherLang(text, ctx.i18n.locale);
    return lang ? _jsx("span", { lang: lang, children: text }) : text;
}
/** A stat's card: a button when it narrows the list, a plain block when it only reports. */
function Tile({ pick, className, title, children, ...attrs }) {
    return pick
        ? _jsx("button", { type: "button", onClick: pick, title: title, "data-stat-pick": "", className: cx(className, 'w-full text-start transition-colors hover:border-brand/60 hover:bg-muted/40', FOCUS), ...attrs, children: children })
        : _jsx("div", { className: className, ...attrs, children: children });
}
/**
 * The figures above a list.
 *
 * Every one is counted over all the rows, never the narrowed ones — see derive.ts. The number is
 * large and tabular so a column of them lines up; the label is small and above it, because the
 * figure is what the eye should land on.
 */
export function StatCards({ stats, ctx, onPick }) {
    if (!stats.length)
        return null;
    const locale = numbersIn(ctx.i18n.locale);
    return (
    // A strip, not a tile: the mark, the label and the figure on one line. Tiles a hundred pixels
    // tall were the largest thing above every list, and pushed the rows they summarise below the
    // fold on a laptop.
    _jsx("div", { className: "grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-2.5", "data-stats": true, children: stats.map((stat) => {
            const tone = (stat.tone ?? 'neutral');
            return (
            // A figure that counts by a condition is a way into the rows it counted: the card is a
            // button that narrows the list to them, and says so for a reader. One that counts every
            // row is a figure only — there is nothing to narrow to.
            _jsxs(Tile, { "data-stat": stat.key, pick: onPick && stat.where ? () => onPick(stat) : undefined, title: onPick && stat.where ? ctx.i18n.t('list.stat.narrow') : undefined, className: "flex items-center gap-3 rounded-xl border bg-card px-3.5 py-2 shadow-card", children: [stat.icon && ctx.icons?.[stat.icon] ? (_jsx("span", { "data-stat-icon": stat.icon, className: `flex size-8 shrink-0 items-center justify-center rounded-full [&_svg]:size-4 ${tone === 'neutral' ? 'bg-foreground text-background' : TONE_CLASS[tone] ?? TONE_CLASS.neutral}`, children: _jsx(Mark, { name: stat.icon, ctx: ctx }) })) : null, _jsxs("div", { className: "grid min-w-0 flex-1", children: [_jsx("p", { className: cx(LABEL, 'truncate'), dir: "auto", children: resolveLabel(stat.label, stat.label_key, ctx.i18n) }), stat.detail ? _jsx("p", { dir: "auto", className: "truncate text-[12px] text-muted-foreground", children: resolveLabel(stat.detail, stat.detail_key, ctx.i18n) }) : null] }), _jsx("p", { "data-stat-value": true, className: "shrink-0 text-[22px] font-bold leading-none tabular-nums text-foreground", children: stat.aggregate === 'oldest'
                            ? sinceWords(stat.since, ctx.i18n)
                            : stat.aggregate === 'percent'
                                ? `${stat.value.toLocaleString(locale)}%`
                                : stat.aggregate === 'sum'
                                    ? stat.value.toLocaleString(locale, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                                    : stat.value.toLocaleString(locale) })] }, stat.key));
        }) }));
}
/**
 * The tabs, with their counts.
 *
 * A real tablist: one stop in the tab order, and the arrows move between the tabs inside it, which
 * is what a person who navigates by keyboard expects of anything shaped like this. The arrows
 * follow the writing direction, so in Arabic the right arrow moves towards the next tab, which is
 * the one to its left.
 */
export function TabStrip({ tabs, by, valuesKey, ctx, current, onPick, inline = false }) {
    if (!tabs.length)
        return null;
    const locale = numbersIn(ctx.i18n.locale);
    const at = tabs.findIndex((t) => t.index === current);
    const move = (event) => {
        const forward = ctx.i18n.dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
        const back = ctx.i18n.dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft';
        const step = event.key === forward ? 1 : event.key === back ? -1 : event.key === 'Home' ? -tabs.length : event.key === 'End' ? tabs.length : 0;
        if (!step)
            return;
        event.preventDefault();
        const next = Math.min(tabs.length - 1, Math.max(0, (at < 0 ? 0 : at) + step));
        onPick(tabs[next].index);
        // The focus goes with the choice: left on the old tab, it sat on a tab out of the tab order
        // (keyboard review, 2026-10-27). Every tab is already drawn, so it is there to focus.
        event.currentTarget.querySelectorAll('[role="tab"]')[next]?.focus();
    };
    return (_jsx("div", { role: "tablist", "data-tabs": true, onKeyDown: move, className: cx('flex flex-wrap items-center gap-[22px]', !inline && 'border-b'), children: tabs.map((tab) => {
            const chosen = tab.index === current;
            return (_jsxs("button", { type: "button", role: "tab", "aria-selected": chosen, tabIndex: chosen ? 0 : -1, "data-tab": tab.index === ALL ? 'all' : tab.index === CLOSED ? 'closed' : tab.group ?? String(tab.value), onClick: () => onPick(tab.index), className: `-mb-px inline-flex items-center gap-[7px] border-b-2 pb-2.5 text-[14.5px] transition-colors pointer-coarse:min-h-11 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring ${chosen
                    ? 'border-brand font-semibold text-brand'
                    : 'border-transparent text-muted-foreground hover:text-foreground'}`, children: [_jsx("span", { dir: "auto", children: tab.index === ALL ? ctx.i18n.t('list.all') : tab.index === CLOSED ? ctx.i18n.t('list.closed') : tab.group ? resolveLabel(tab.label ?? tab.group, tab.label_key, ctx.i18n) : tab.label ?? valueLabel({ key: by, label: '', ...(valuesKey ? { values_key: valuesKey } : {}) }, tab.value, ctx.i18n) }), _jsx("span", { "data-tab-count": true, className: "text-[13px] tabular-nums text-muted-foreground", children: tab.count.toLocaleString(locale) })] }, tab.index));
        }) }));
}
/**
 * The actions on one row: the first two as buttons, the rest behind a `⋯`.
 *
 * Two is the limit because People declares eight of these, and at three the action cell was the
 * widest thing on a six-column row — the buttons outweighed the person they were about. The
 * alternative, shrinking them all to unlabelled glyphs, turns a row into a guessing game. The
 * ones that stay out are the ones declared last, so a manifest decides what is worth a button by
 * the order it writes them in.
 *
 * `<details>` rather than a popover: this package is loaded by a browser and carries no
 * dependencies, and the element already opens, closes, takes focus and answers the keyboard
 * without any of that being written here. Every action keeps its `data-row-action` whichever side
 * of the fold it lands on, so a test written before the fourth action existed still finds it.
 */
const ACTIONS_SHOWN = 2;
/**
 * How many of a table row's actions stay buttons: none on a phone, where two took the width its
 * three columns need — except on a person's own list (`audience: "self"`), whose one primary row
 * action (the first, `rowActionOrder`) stays on the row, because an employee on a phone came to the
 * row for it and ⋯ hid it (HR review 2026-10-27); one on a middle screen; else ACTIONS_SHOWN.
 */
export function rowActionsInline(manifest, phone, middle) {
    if (phone)
        return { inline: manifest.audience === 'self' ? 1 : 0 };
    return middle ? { inline: 1 } : {};
}
/**
 * Whether a row action has somewhere to go from this row. A request needs its path filled from the
 * row; a page needs the row's key to say which record it is about. The page case was answered by
 * the path check alone, and an action with no request has no path — so "Open" was never drawn.
 */
function reachable(action, row) {
    // An action that opens a registered component has no path to fill: it is handed the row itself
    // (ActionEditor), so the row's own values decide nothing here but `when`.
    if (action.component)
        return true;
    if (action.screen) {
        const id = row[action.row_key ?? 'id'];
        return id !== undefined && id !== null && id !== '';
    }
    return Boolean(pathFor(action, row));
}
/**
 * The order a row's actions are offered in: the manifest's, except that a destructive one is never
 * first (L-16). A manifest that lists «reverse» before «copy», or «retire» before «edit», still
 * shows the harmless one on the row's edge and the destructive one after it.
 */
export function rowActionOrder(actions) {
    return [...actions.filter((a) => !a.destructive), ...actions.filter((a) => a.destructive)];
}
export function RowActions({ actions, row, ctx, onRun, className = '', inline = ACTIONS_SHOWN, name }) {
    const applicable = rowActionOrder(actions.filter((a) => appliesTo(a, row) && reachable(a, row)));
    if (!applicable.length)
        return null;
    const shown = applicable.slice(0, inline);
    const folded = applicable.slice(inline);
    // The first action is the one a person came to the row for, so it keeps an edge; the second is
    // quiet until the row is under the pointer or the keyboard, and never invisible to either.
    return (_jsxs("span", { className: `inline-flex flex-wrap items-center gap-1 ${className}`, children: [shown.map((a, i) => (_jsx(Button, { size: "sm", variant: i === 0 ? 'secondary' : 'quiet', "data-row-action": a.key, onClick: () => onRun?.(a, row), ...(name ? { 'aria-label': ctx.i18n.t('action.onRow', { action: resolveLabel(a.label, a.label_key, ctx.i18n), name }) } : {}), className: "gap-1.5", icon: _jsx(Mark, { name: a.icon, ctx: ctx, className: "size-4" }), children: resolveLabel(a.label, a.label_key, ctx.i18n) }, a.key))), folded.length ? (
            // Picking from the fold closes it — Menu does that — so the menu you chose from is not
            // still hanging open when the dialog you opened has come and gone.
            _jsx(Menu, { trigger: "\u22EF", label: name ? ctx.i18n.t('action.onRow', { action: ctx.i18n.t('action.more'), name }) : ctx.i18n.t('action.more'), "data-row-overflow": "", items: folded.map((a) => ({ key: a.key, label: resolveLabel(a.label, a.label_key, ctx.i18n), icon: _jsx(Mark, { name: a.icon, ctx: ctx, className: "size-4" }), onPick: () => onRun?.(a, row), attrs: { 'data-row-action': a.key } })) })) : null] }));
}
/**
 * The rows as cards.
 *
 * The same data attributes a table carries — the screen, each row, each column, each row action —
 * so a test written against one layout keeps working against the other. They are what proves a
 * screen shows what it claims to; tying them to a `<td>` would mean the proof disappeared the day
 * somebody chose cards.
 */
export function CardGrid({ manifest, rows, ctx, rowActions, onRun, controls = {} }) {
    const card = manifest.card;
    const columns = new Map((manifest.columns ?? []).map((c) => [c.key, c]));
    const kept = useRef(null);
    useRowFocusKeeper(kept, rows.map((row, i) => rowKey(row, i)));
    if (!card)
        return _jsx(ListTable, { manifest: manifest, rows: rows, ctx: ctx, ...(rowActions ? { rowActions } : {}), ...(onRun ? { onRun } : {}) });
    const labels = (manifest.columns ?? []).filter((c) => !c.hidden);
    if (!rows.length) {
        // The list's empty block (empty, narrowed, a catalogue it waits on) is drawn the same as under
        // a table — the column names over nothing said neither what the emptiness meant nor what fills it.
        if (controls.empty) {
            return (_jsx("div", { className: "rounded-xl border bg-card shadow-card", "data-screen": manifest.screen, "data-layout": "cards", "data-empty": "", children: controls.empty }));
        }
        return (_jsx("div", { className: "rounded-xl border bg-card shadow-card", "data-screen": manifest.screen, "data-layout": "cards", "data-empty": "", children: _jsx("div", { className: "flex flex-wrap gap-5 border-b px-5 py-4 text-sm text-muted-foreground", children: labels.map((c) => (_jsx("span", { dir: "auto", className: "font-medium", children: resolveLabel(c.label, c.label_key, ctx.i18n) }, c.key))) }) }));
    }
    const perRow = onRun ? (rowActions ?? []).filter((a) => runnable(a) && (!a.permission || ctx.holds(a.permission))) : [];
    // What a card is called — its title, else the first column — said with its box and each of its
    // buttons: eleven cards each with «Select» and «Edit» named no card (P08-06), as a table's rows do.
    const named = (card.title ? columns.get(card.title) : undefined) ?? labels[0];
    const nameOf = (row) => (named ? cellWords(row, named, ctx) : '');
    const cell = (key, row) => {
        if (!key)
            return null;
        const column = columns.get(key);
        if (!column)
            return null;
        return _jsx("span", { "data-column": key, children: _jsx(Cell, { value: row[key], column: column, ctx: ctx, row: row }) });
    };
    return (_jsxs("div", { className: "grid gap-3", "data-layout": "cards", children: [_jsx("div", { ref: kept, className: "grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] items-stretch gap-3", "data-screen": manifest.screen, "data-layout": "cards", children: rows.map((row, i) => {
                    const emphasis = emphasisOf(manifest, row);
                    const said = emphasis ? resolveLabel(emphasis.label, emphasis.label_key, ctx.i18n) : '';
                    return (_jsxs("div", { "data-row": rowKey(row, i), ...(controls.selected?.has(rowKey(row, i)) ? { 'aria-selected': true } : {}), ...(emphasis ? { 'data-emphasis': emphasis.tone, title: said } : {}), className: `group/row relative flex h-full flex-col gap-[9px] rounded-xl border bg-card p-4 shadow-card transition-colors hover:bg-muted/30 ${controls.selected?.has(rowKey(row, i)) ? 'border-brand/50 bg-brand/6' : ''} ${emphasis ? CARD_EDGE[emphasis.tone] : ''}`, children: [emphasis ? _jsx("span", { className: "sr-only", "data-emphasis-label": "", children: said }) : null, controls.selected ? (_jsx("span", { className: "absolute end-3 top-3", children: _jsx(Checkbox, { "data-pick": rowKey(row, i), "aria-label": nameOf(row) ? ctx.i18n.t('list.pickRow', { name: nameOf(row) }) : ctx.i18n.t('list.pick'), checked: controls.selected.has(rowKey(row, i)), onChange: (e) => controls.onSelect?.([rowKey(row, i)], e.currentTarget.checked) }) })) : null, _jsxs("div", { className: "flex items-start gap-3", children: [card.avatar ? (() => {
                                        const text = String(row[card.avatar] ?? '');
                                        // A declared colour beats a hashed one. The hash is honest for a person — it tells
                                        // two rows apart and classifies nothing — but where somebody has chosen a colour,
                                        // that choice is data and the hash would be a machine talking over them.
                                        const chosen = card.tone ? String(row[card.tone] ?? '') : '';
                                        const tone = TONES.includes(chosen) ? chosen : hueOf(text);
                                        return (_jsx("span", { "data-avatar": true, "aria-hidden": "true", className: `flex size-10 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${TONE_CLASS[tone]}`, children: initialsOf(text) }));
                                    })() : null, _jsxs("div", { className: "flex min-w-0 flex-1 items-start justify-between gap-2", children: [_jsxs("div", { className: "min-w-0", children: [_jsx("h2", { dir: "auto", className: "line-clamp-2 wrap-anywhere text-base font-semibold leading-tight text-foreground", children: controls.open ? _jsx(OpenLink, { row: row, open: controls.open, ctx: ctx, name: card.title && columns.get(card.title) ? cellWords(row, columns.get(card.title), ctx) : '', children: cell(card.title, row) }) : cell(card.title, row) }), card.subtitle ? _jsx("p", { className: "truncate text-xs text-muted-foreground", children: cell(card.subtitle, row) }) : null] }), card.badge ? _jsx("span", { className: "shrink-0", children: cell(card.badge, row) }) : null] })] }), card.description ? _jsx("p", { dir: "auto", className: "mt-4 text-sm leading-relaxed text-muted-foreground", children: cell(card.description, row) }) : null, card.stats?.length ? (_jsx("div", { className: "mt-auto grid grid-cols-3 gap-3 border-t pt-4", style: { gridTemplateColumns: `repeat(${Math.min(card.stats.length, 3)}, minmax(0, 1fr))` }, children: card.stats.map((key) => {
                                    // A figure is large and tabular; a phrase is not. "identity 5" set at the size of
                                    // a number reads as a heading that ran out of room, and truncating it loses the
                                    // half that carries the meaning.
                                    const type = columns.get(key)?.type;
                                    const numeric = type === 'number' || type === 'money' || type === 'count' || type === 'bytes' || type === 'percent';
                                    return (_jsxs("div", { className: "min-w-0", children: [_jsx("div", { className: SMALL_LABEL, dir: "auto", children: resolveLabel(columns.get(key)?.label, columns.get(key)?.label_key, ctx.i18n) }), _jsx("div", { dir: "auto", className: `mt-1 text-foreground ${numeric ? 'truncate text-xl font-bold tabular-nums' : 'line-clamp-3 wrap-anywhere text-sm font-medium'}`, children: cell(key, row) })] }, key));
                                }) })) : null, perRow.length ? (_jsx("div", { className: card.stats?.length ? 'mt-4' : 'mt-auto border-t pt-4', children: _jsx(RowActions, { actions: perRow, row: row, ctx: ctx, ...(onRun ? { onRun } : {}), ...(nameOf(row) ? { name: nameOf(row) } : {}) }) })) : null] }, rowKey(row, i)));
                }) }), controls.footer ? _jsx("div", { className: "rounded-xl border bg-card", children: controls.footer }) : null] }));
}
/** The key a row is remembered by across a sort, a page and a reload: its id, or failing that its content. */
/**
 * A row's emphasis edge: three pixels of the tone inside the start of its first cell, drawn as an
 * inset shadow so the row does not move by the width of a border. Mirrored for right to left.
 */
const CARD_EDGE = { red: 'border-s-[3px] border-s-tone-red', amber: 'border-s-[3px] border-s-tone-amber' };
const EMPHASIS_EDGE = {
    red: 'shadow-[inset_3px_0_0_0_var(--tone-red)] rtl:shadow-[inset_-3px_0_0_0_var(--tone-red)]',
    amber: 'shadow-[inset_3px_0_0_0_var(--tone-amber)] rtl:shadow-[inset_-3px_0_0_0_var(--tone-amber)]',
};
export function rowKey(row, i) {
    const id = row['id'] ?? row['key'] ?? row['event_id'] ?? row['membership_id'];
    if (id !== undefined && id !== null)
        return String(id);
    try {
        return JSON.stringify(row);
    }
    catch {
        return String(i ?? 0);
    }
}
/** Whether a click landed on something with its own job — a button, a link, a box — rather than on the row. */
function onControl(target) {
    const el = target;
    return Boolean(el?.closest?.('a,button,input,select,textarea,label,[role="menu"],[role="menuitem"],[data-menu-panel]'));
}
/** The first cell of a row that is a record: the words, as a link to its page. */
export function OpenLink({ row, open, name, ctx, className, children }) {
    const href = open.href(row);
    if (!href)
        return _jsx(_Fragment, { children: children });
    // The row's own way in carries the same mark as every other name that opens a record.
    return (_jsx(RecordLink, { href: href, tone: "open", "data-row-open": "", openLabel: openRecordLabel(ctx.i18n, name), rtl: ctx.i18n.dir === 'rtl', ...(className ? { className } : {}), onClick: (e) => { if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
            return; e.preventDefault(); open.onOpen(row); }, children: children }));
}
/**
 * The width at which a list is a phone's: under the `sm` breakpoint, 640px. The window's width and
 * not the table's, on purpose: a list kept mounted but hidden under a form page measures nothing,
 * and a table's own width would read that as the narrowest screen there is and fold the columns
 * behind the person's back. On the server, and in a test's static render, there is no window: wide.
 */
const PHONE = '(max-width: 639.98px)';
function subscribeNarrow(change) {
    if (typeof window === 'undefined' || !window.matchMedia)
        return () => { };
    const query = window.matchMedia(PHONE);
    query.addEventListener('change', change);
    return () => query.removeEventListener('change', change);
}
/** Whether the screen is a phone's, and every list on it draws `COLUMNS_NARROW` columns and no selection. */
export function useNarrowScreen() {
    return useSyncExternalStore(subscribeNarrow, () => typeof window !== 'undefined' && Boolean(window.matchMedia) && window.matchMedia(PHONE).matches, () => false);
}
/**
 * Under 1024px — a tablet, or a desktop window at 200% — a row keeps its first action and folds the
 * rest into ⋯: the quiet second one, «إضافة شخص مسؤول» at 144px, spilled out of a 74px actions cell
 * over the next column at 720px (low-vision review, 2026-10-27).
 */
const MIDDLE = '(max-width: 1023.98px)';
function subscribeMiddle(change) {
    if (typeof window === 'undefined' || !window.matchMedia)
        return () => { };
    const query = window.matchMedia(MIDDLE);
    query.addEventListener('change', change);
    return () => query.removeEventListener('change', change);
}
export function useMiddleScreen() {
    return useSyncExternalStore(subscribeMiddle, () => typeof window !== 'undefined' && Boolean(window.matchMedia) && window.matchMedia(MIDDLE).matches, () => false);
}
/** How many data columns a list draws on this screen. */
export function columnLimitFor(narrow) {
    return narrow ? COLUMNS_NARROW : COLUMNS_WIDE;
}
/** The three-state sort handle a header shows. */
function SortMark({ dir }) {
    const d = dir === 'ascending' ? 'M4 10l4-4 4 4' : dir === 'descending' ? 'M4 6l4 4 4-4' : 'M5 6.5l3-3 3 3M5 9.5l3 3 3-3';
    return _jsx("svg", { width: "14", height: "14", viewBox: "0 0 16 16", fill: "none", stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true, className: dir === 'none' ? 'opacity-35' : 'text-brand', children: _jsx("path", { d: d }) });
}
/**
 * A column heading: the label, a sort handle when the column is sortable, and a menu of what can
 * be done with the column. `aria-sort` is on the `<th>`, which is where a reader looks for it.
 */
/**
 * The currency of a money column as its head says it — «SAR», «ر.س.» — or undefined for a column
 * that names none (or one Intl does not know). A table writes it once, in the head, and its cells
 * the bare amounts (T-8): «SAR» on every row was the word a phone cut, or laid over the badge beside it.
 */
export function currencyWord(column, locale) {
    if (column.type !== 'money' || !column.currency)
        return undefined;
    try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency: column.currency }).formatToParts(0).find((p) => p.type === 'currency')?.value;
    }
    catch {
        return undefined;
    }
}
/** The column a table's cells are drawn from: a money column without its currency, which its head carries. */
function inTable(column, locale) {
    if (!currencyWord(column, locale))
        return column;
    const { currency: _inHead, ...rest } = column;
    return rest;
}
/**
 * The width a column needs on a phone, or undefined for one that may wrap. A badge and an amount
 * do not break — a status is one pill, a figure one run of digits — and in the phone's fixed layout
 * an equal share of the width cut them or laid them over the next cell (HR review, self-service
 * payslips). So they get their natural width, measured from the longest value on the page in `ch`
 * plus the cell's and the pill's own padding, and the columns that wrap share what is left.
 */
export function naturalWidth(column, rows, i18n) {
    const badge = column.display === 'badge' && column.untoned !== 'plain';
    const amount = column.type === 'money' || column.type === 'percent';
    if (!badge && !amount)
        return undefined;
    const plain = inTable(column, numbersIn(i18n.locale));
    const longest = Math.max(0, ...rows.map((row) => {
        const value = row[column.key];
        if (value === null || value === undefined || value === '')
            return 1;
        return (badge ? valueLabel(plain, value, i18n) : formatCell(value, plain, i18n)).length;
    }));
    if (!longest)
        return undefined;
    // At most sixteen characters: a longer status wraps as text would rather than take the row.
    return `calc(${Math.min(longest, 16)}ch + ${badge ? '2.75rem' : '1rem'})`;
}
function ColumnHead({ column, ctx, controls }) {
    const label = resolveLabel(column.label, column.label_key, ctx.i18n);
    const currency = currencyWord(column, numbersIn(ctx.i18n.locale));
    const head = currency ? _jsxs(_Fragment, { children: [label, " ", _jsxs("span", { "data-currency": "", className: "font-normal", children: ["(", currency, ")"] })] }) : label;
    const sortable = controls.sortable?.includes(column.key) && controls.onSort;
    const current = controls.sort ?? '';
    const dir = current === column.key ? 'ascending' : current === `-${column.key}` ? 'descending' : 'none';
    // None → ascending → descending → none, so a third click puts the list back as it arrived.
    const cycle = () => controls.onSort?.(dir === 'none' ? column.key : dir === 'ascending' ? `-${column.key}` : '');
    const filterable = controls.filterable?.includes(column.key) && controls.onFilterColumn;
    const items = [];
    if (sortable) {
        items.push({ key: 'asc', label: ctx.i18n.t('list.sort.asc'), onPick: () => controls.onSort?.(column.key) });
        items.push({ key: 'desc', label: ctx.i18n.t('list.sort.desc'), onPick: () => controls.onSort?.(`-${column.key}`) });
        if (dir !== 'none')
            items.push({ key: 'none', label: ctx.i18n.t('list.sort.none'), onPick: () => controls.onSort?.('') });
    }
    if (filterable)
        items.push({ key: 'filter', label: ctx.i18n.t('list.column.filter'), onPick: () => controls.onFilterColumn?.(column.key) });
    return (_jsx("th", { scope: "col", dir: "auto", "data-column-head": column.key, ...(sortable ? { 'aria-sort': dir } : {}), className: cx('group/th relative py-0 text-start', controls.compact ? 'px-2 text-[12px] font-semibold' : 'px-2.5 font-bold'), children: _jsxs("span", { className: cx('flex items-center gap-1', controls.compact && '[&>span]:min-h-0 [&>span]:py-2'), children: [sortable ? (_jsxs("button", { type: "button", "data-sort": column.key, onClick: cycle, title: ctx.i18n.t('list.sort.by', { column: label }), className: `inline-flex min-h-11 flex-1 items-center gap-1.5 py-3 text-start font-bold transition-colors hover:text-foreground ${FOCUS} ${dir === 'none' ? '' : 'text-foreground'}`, children: [head, _jsx(SortMark, { dir: dir })] })) : _jsx("span", { className: "inline-flex min-h-11 flex-1 items-center py-3", children: head }), items.length ? (_jsx("span", { className: "opacity-0 transition-opacity group-hover/th:opacity-100 group-focus-within/th:opacity-100 [details[open]&]:opacity-100", children: _jsx(Menu, { trigger: "\u22EF", label: ctx.i18n.t('list.column.options', { column: label }), items: items, "data-column-menu": column.key }) })) : null] }) }));
}
/**
 * The head of the expand column, and the one place the folded columns can still be sorted and
 * filtered from: a column in the panel has no head to hold its handle, and a list that could be
 * sorted by the joining date yesterday must not lose that because the date moved under the row.
 * The same `⋯` menu a head carries, one entry per folded column that allows it; shown always,
 * because unlike a head's there is no label beside it to hover first.
 */
function FoldedHead({ folded, ctx, controls }) {
    const current = controls.sort ?? '';
    const items = [];
    let sortedHere = false;
    for (const column of folded) {
        const label = resolveLabel(column.label, column.label_key, ctx.i18n);
        if (controls.sortable?.includes(column.key) && controls.onSort) {
            if (current === column.key || current === `-${column.key}`)
                sortedHere = true;
            items.push({ key: `asc:${column.key}`, label: ctx.i18n.t('list.folded.asc', { column: label }), onPick: () => controls.onSort?.(column.key), attrs: { 'data-folded-sort': column.key } });
            items.push({ key: `desc:${column.key}`, label: ctx.i18n.t('list.folded.desc', { column: label }), onPick: () => controls.onSort?.(`-${column.key}`), attrs: { 'data-folded-sort': `-${column.key}` } });
        }
        if (controls.filterable?.includes(column.key) && controls.onFilterColumn) {
            items.push({ key: `filter:${column.key}`, label: ctx.i18n.t('list.folded.filter', { column: label }), onPick: () => controls.onFilterColumn?.(column.key), attrs: { 'data-folded-filter': column.key } });
        }
    }
    if (sortedHere)
        items.push('divider', { key: 'none', label: ctx.i18n.t('list.sort.none'), onPick: () => controls.onSort?.('') });
    return (_jsxs("th", { scope: "col", className: "w-10 px-1 py-0 text-center", "data-expand-head": "", children: [_jsx("span", { className: "sr-only", children: ctx.i18n.t('list.expand.head') }), items.length ? (
            // Blue when the list is sorted by a column that has no head to say so: where you are.
            _jsx(Menu, { trigger: "\u22EF", align: "start", label: ctx.i18n.t('list.folded.options'), items: items, "data-folded-menu": "", ...(sortedHere ? { 'data-folded-sorted': '' } : {}), className: cx('inline-block', sortedHere && '[&>[data-menu-trigger]]:text-brand') })) : null] }));
}
/**
 * The columns a row folded, opened under it: a label and a value each, in a grid that takes as
 * many as fit. Every value is the cell it would have been — the same `Cell`, so a status is its
 * badge, money is money and a day carries its Hijri twin — and the value carries `data-column`
 * as its cell did, so what finds a value by its column still finds it here. Nothing is a blank:
 * an empty value reads «—».
 */
function FoldedPanel({ id, rowKey: key, row, folded, span, ctx }) {
    const blank = (v) => v === '' || (Array.isArray(v) && v.length === 0);
    return (_jsx("tr", { id: id, "data-row-panel": key, className: "border-b bg-muted/40 last:border-0", children: _jsx("td", { colSpan: span, className: "p-0", children: _jsx("dl", { className: "sticky start-0 grid w-[100cqw] max-w-full grid-cols-[repeat(auto-fill,minmax(11rem,1fr))] gap-x-6 gap-y-3 px-4 pb-4 pt-2", children: folded.map((c) => (_jsxs("div", { className: "grid min-w-0 content-start gap-1", "data-panel-item": c.key, children: [_jsx("dt", { dir: "auto", className: SMALL_LABEL, children: resolveLabel(c.label, c.label_key, ctx.i18n) }), _jsx("dd", { dir: "auto", "data-column": c.key, className: cx('min-w-0 break-words text-[14.5px] text-foreground', (c.type === 'number' || c.type === 'money' || c.type === 'bytes' || c.type === 'count' || c.type === 'percent') && 'tabular-nums'), children: blank(row[c.key]) && c.display !== 'person' ? '—' : _jsx(Cell, { value: row[c.key], column: c, ctx: ctx, row: row }) })] }, c.key))) }) }) }));
}
/**
 * Whether a table wider than its scroll box has columns still hidden under the actions held at
 * its end: it overflows, and it is not scrolled all the way to that end. `scrollLeft` runs from 0
 * towards the end in either direction — positive in a left-to-right page, negative in Arabic,
 * where the end is the left — so its size is how far the box has scrolled. A pixel of slack,
 * because a zoomed page reports fractions.
 */
export function columnsUnderEnd(scrollWidth, clientWidth, scrollLeft) {
    const room = scrollWidth - clientWidth;
    return room > 1 && Math.abs(scrollLeft) < room - 1;
}
/**
 * Marks the scroll box `data-under-end` while columns sit under its sticky actions, so their cell
 * draws an edge on its leading side: without one, the last column slid beneath the buttons and
 * nothing said there was more. Set on the element, not through state — a scroll does not
 * re-render the table. One function for every table, so React attaches it once (a callback ref
 * that returns its own cleanup).
 */
export function watchUnderEnd(box) {
    if (!box)
        return undefined;
    const mark = () => {
        box.toggleAttribute('data-under-end', columnsUnderEnd(box.scrollWidth, box.clientWidth, box.scrollLeft));
        // A control the keyboard reaches is scrolled clear of the actions column held at the end, not
        // under it: a column's options button sat wholly behind it at 200% (low-vision review, 2026-10-27).
        const held = box.querySelector?.('[data-row-actions-head]');
        if (box.style)
            box.style.scrollPaddingInlineEnd = held ? `${held.getBoundingClientRect().width}px` : '';
    };
    mark();
    box.addEventListener('scroll', mark, { passive: true });
    const resized = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(mark);
    resized?.observe(box);
    if (box.firstElementChild)
        resized?.observe(box.firstElementChild);
    return () => { box.removeEventListener('scroll', mark); resized?.disconnect(); };
}
/** The edge the actions cell draws on its leading side while columns sit under it: a hairline in
 *  the border's colour and a soft shade over what scrolls beneath. Leading is the left in English
 *  and the right in Arabic, as the row's emphasis edge is. */
const UNDER_END_EDGE = '[[data-under-end]_&]:shadow-[inset_1px_0_0_0_var(--border),-8px_0_8px_-8px_color-mix(in_oklab,var(--foreground)_18%,transparent)] '
    + 'rtl:[[data-under-end]_&]:shadow-[inset_-1px_0_0_0_var(--border),8px_0_8px_-8px_color-mix(in_oklab,var(--foreground)_18%,transparent)]';
export function ListTable({ manifest, rows, ctx, rowActions, onRun, total, controls = {} }) {
    // A hidden column is declared so it can be named — as a person's second line, as something the
    // search matches, as a monogram's colour — and drawing it as well would show the same value
    // twice on one row. Of the rest, five are heads (three on a phone) and the others fold into a
    // panel under the row: seventy lists had more than a screen holds, and a table that scrolls
    // sideways hides the actions at its end and the status a person came to read (columnPlan).
    const narrow = useNarrowScreen();
    const middle = useMiddleScreen();
    const limit = controls.columnLimit ?? columnLimitFor(narrow);
    const { heads: planned, folded } = columnPlan(manifest, limit);
    // A money column's currency is said once, in its head; its cells are the amounts (T-8).
    const columns = planned.map((c) => inTable(c, numbersIn(ctx.i18n.locale)));
    // Laid out to the screen only while it draws the few a phone gets; every column drawn on a phone
    // scrolls sideways like any wide table. A row's actions fold into ⋯ on a phone either way.
    const compact = limit <= COLUMNS_NARROW;
    const phone = compact || narrow;
    // More heads than the few: the person opened every column.
    const wide = !compact && columns.length > COLUMNS_WIDE;
    const [ownOpen, setOwnOpen] = useState(() => new Set());
    const open = controls.expanded ?? ownOpen;
    const expand = controls.onExpand ?? ((ks, on) => setOwnOpen((was) => { const next = new Set(was); for (const k of ks)
        on ? next.add(k) : next.delete(k); return next; }));
    const uid = useId();
    const perRow = onRun ? (rowActions ?? []).filter((a) => runnable(a) && (!a.permission || ctx.holds(a.permission))) : [];
    // Zero rows is still a table: the headers are the screen's columns, and replacing them with
    // "Nothing here yet" hides the shape of the list the person came to fill.
    // The selection column exists only when something consumes it: a checkbox with nothing to run
    // on the selection is noise. The header box selects the rows of this page and no more — what
    // cannot be seen cannot be selected — and it reads "some" when only some are.
    const selected = controls.selected;
    const keys = rows.map((row, i) => rowKey(row, i));
    // A row decided from its own button leaves the list; the keyboard goes to the row after it (P08-05).
    const kept = useRef(null);
    useRowFocusKeeper(kept, keys);
    const onPage = selected ? keys.filter((k) => selected.has(k)).length : 0;
    // Every column the table has, for a row that spans them all: the empty block and a row's panel.
    const span = columns.length + (selected ? 1 : 0) + (folded.length ? 1 : 0) + (perRow.length ? 1 : 0);
    const panelId = (i) => `${uid}-panel-${i}`;
    const natural = compact ? columns.map((c) => naturalWidth(c, rows, ctx.i18n)) : [];
    return (
    // `min-w-0` here, on the card and not on the page. A grid item's automatic minimum is its
    // content, so this card grew to the table's full width inside whatever grid it sat in — and
    // the `overflow-x-auto` below, which is the thing meant to scroll, was exactly as wide as the
    // table and so never scrolled. The page scrolled instead. Fixing it on the page's containers
    // only moved the break one level down: every screen draws this card inside a grid of its
    // own, and the next screen with another grid would have broken it again. The card is the one
    // element every list shares, so the minimum is set where the overflow starts.
    _jsxs("div", { ref: kept, className: cx('min-w-0', !controls.compact && 'rounded-xl border bg-card'), "data-screen": manifest.screen, ...(rows.length ? {} : { 'data-empty': '' }), children: [_jsx("div", { ref: watchUnderEnd, className: "@container relative overflow-x-auto", children: _jsxs("table", { className: cx('w-full', controls.compact ? 'text-[13.5px]' : 'text-[14.5px]', compact && 'table-fixed [&_td]:whitespace-normal [&_th]:whitespace-normal [&_td]:wrap-anywhere [&_td]:px-1.5 [&_th]:px-1.5', wide && '[&_td]:min-w-[7.5rem] [&_th]:min-w-[7.5rem] [&_td]:max-w-[18rem] [&_td_bdi]:line-clamp-3'), "data-list-table": true, ...(wide ? { 'data-columns-wide': '' } : {}), ...(manifest.title || manifest.title_key ? { 'aria-label': resolveLabel(manifest.title, manifest.title_key, ctx.i18n) } : {}), children: [compact ? (_jsxs("colgroup", { children: [selected ? _jsx("col", { className: "w-10" }) : null, folded.length ? _jsx("col", { className: "w-10" }) : null, columns.map((c, i) => {
                                    const width = natural[i];
                                    return _jsx("col", { ...(width ? { 'data-natural': '', style: { width } } : {}), className: i === 0 && !natural.some(Boolean) ? 'w-[38%]' : undefined }, c.key);
                                }), perRow.length ? _jsx("col", { className: "w-11" }) : null] })) : null, _jsx("thead", { className: cx('border-b text-muted-foreground', controls.compact ? 'text-[12px] font-semibold' : 'text-[13px] font-bold'), children: _jsxs("tr", { children: [selected ? (_jsx("th", { scope: "col", className: "w-10 px-2.5 py-3 text-center", children: _jsx(Checkbox, { "aria-label": ctx.i18n.t('list.pickPage'), "data-pick-page": "", checked: rows.length > 0 && onPage === rows.length, indeterminate: onPage > 0 && onPage < rows.length, disabled: !rows.length, onChange: (e) => controls.onSelect?.(keys, e.currentTarget.checked) }) })) : null, folded.length ? _jsx(FoldedHead, { folded: folded, ctx: ctx, controls: controls }) : null, planned.map((c) => _jsx(ColumnHead, { column: c, ctx: ctx, controls: controls }, c.key)), perRow.length ? _jsx("th", { scope: "col", "data-row-actions-head": "", className: cx('sticky end-0 bg-card px-2.5 py-3', UNDER_END_EDGE, compact && 'w-11 px-1'), children: _jsx("span", { className: "sr-only", children: ctx.i18n.t('action.forThisRow') }) }) : null] }) }), _jsxs("tbody", { children: [!rows.length && controls.empty ? (_jsx("tr", { "data-empty-row": true, children: _jsx("td", { colSpan: span, className: "p-0", children: _jsx("div", { className: "sticky start-0 w-[100cqw]", children: controls.empty }) }) })) : null, rows.map((row, i) => {
                                    const key = keys[i];
                                    const isOpen = folded.length > 0 && open.has(key);
                                    // A declared emphasis: a tone at the row's start edge, on whichever cell comes first,
                                    // and its words for a reader and on hover — a tone is never colour alone.
                                    const emphasis = emphasisOf(manifest, row);
                                    const said = emphasis ? resolveLabel(emphasis.label, emphasis.label_key, ctx.i18n) : '';
                                    const edge = emphasis ? EMPHASIS_EDGE[emphasis.tone] : '';
                                    const edgeOn = selected ? 'pick' : folded.length ? 'expand' : 'data';
                                    return (_jsxs(Fragment, { children: [_jsxs("tr", { "data-row": key, ...(selected?.has(key) ? { 'aria-selected': true } : {}), ...(emphasis ? { 'data-emphasis': emphasis.tone, title: said } : {}), ...(controls.open ? { onClick: (e) => { if (!onControl(e.target))
                                                        controls.open.onOpen(row); } } : {}), 
                                                // An open row and its panel read as one thing: no rule between them, the rule under the panel.
                                                className: cx('group/row transition-colors hover:bg-muted/40', controls.open && 'cursor-pointer', !isOpen && (controls.compact ? 'border-b border-border/60' : 'border-b last:border-0'), selected?.has(key) ? 'bg-brand/6' : isOpen && 'bg-muted/40'), children: [selected ? (_jsx("td", { className: cx('px-2.5 py-2 text-center', edgeOn === 'pick' && edge), children: _jsx(Checkbox, { "data-pick": key, "aria-label": columns[0] ? ctx.i18n.t('list.pickRow', { name: cellWords(row, columns[0], ctx) }) : ctx.i18n.t('list.pick'), checked: selected.has(key), onChange: (e) => controls.onSelect?.([key], e.currentTarget.checked) }) })) : null, folded.length ? (_jsx("td", { className: cx("px-1 py-2 text-center", controls.compact ? "w-8" : "w-10", edgeOn === 'expand' && edge), children: _jsx(IconButton, { size: "sm", "data-expand": key, "aria-expanded": isOpen, "aria-controls": panelId(i), label: ctx.i18n.t(isOpen ? 'list.row.collapse' : 'list.row.expand'), onClick: () => expand([key], !isOpen), children: _jsx(Chevron, { className: cx('transition-transform motion-reduce:transition-none', isOpen && 'rotate-180') }) }) })) : null, columns.map((c, ci) => (
                                                    // The cell keeps the page's direction, so every value of a column lines up under its head;
                                                    // the value is isolated in its own (<bdi>) — a «4» in an Arabic table was a left-to-right
                                                    // cell, drawn at the far edge of its column and under the next one's head.
                                                    _jsxs("td", { "data-column": c.key, className: cx('text-start', controls.compact ? 'px-2 py-2.5 text-foreground' : 'px-2.5 py-[11px]', (c.type === 'number' || c.type === 'money' || c.type === 'bytes' || c.type === 'count' || c.type === 'percent') && 'tabular-nums', 
                                                        // A card's first column is what the row is — «رخصة تشغيل رافعة» — and keeps room for a name, not a word a line.
                                                        controls.compact && ci === 0 && !compact && 'min-w-[7rem]', ci === 0 && edgeOn === 'data' && edge), children: [ci === 0 && emphasis ? _jsxs("span", { className: "sr-only", "data-emphasis-label": "", children: [said, " \u2014 "] }) : null, _jsx("bdi", { children: ci === 0 && controls.open && !c.ref
                                                                    ? _jsx(OpenLink, { row: row, open: controls.open, ctx: ctx, name: cellWords(row, c, ctx), children: _jsx(Cell, { value: row[c.key], column: c, ctx: ctx, row: row }) })
                                                                    : _jsx(Cell, { value: row[c.key], column: c, ctx: ctx, row: row }) })] }, c.key))), perRow.length ? (
                                                    // Opaque, so the columns scrolling under it stay hidden; the row's tint is laid over the
                                                    // card as an image, or an open, selected or hovered row showed one white cell at its end.
                                                    _jsx("td", { "data-row-actions": "", className: cx('sticky end-0 bg-card px-2.5 py-2 text-end', UNDER_END_EDGE, selected?.has(key) ? 'bg-linear-to-l from-brand/6 to-brand/6'
                                                            : isOpen ? 'bg-linear-to-l from-muted/40 to-muted/40'
                                                                : 'group-hover/row:bg-linear-to-l group-hover/row:from-muted/40 group-hover/row:to-muted/40'), children: _jsx(RowActions, { actions: perRow, row: row, ctx: ctx, className: "justify-end", ...(columns[0] ? { name: cellWords(row, columns[0], ctx) } : {}), ...(onRun ? { onRun } : {}), ...rowActionsInline(manifest, phone, middle) }) })) : null] }), isOpen ? _jsx(FoldedPanel, { id: panelId(i), rowKey: key, row: row, folded: folded, span: span, ctx: ctx }) : null] }, key));
                                })] }), controls.totals ? (_jsx("tfoot", { "data-totals": "", children: _jsxs("tr", { className: "border-b font-bold text-foreground", children: [selected ? _jsx("td", {}) : null, folded.length ? _jsx("td", {}) : null, columns.map((c, ci) => (_jsx("td", { "data-column": c.key, className: cx('px-2.5 text-start', controls.compact ? 'py-3' : 'py-[11px]', 'tabular-nums'), children: _jsx("bdi", { children: ci === 0 ? controls.totals.label : controls.totals.values[c.key] ?? null }) }, c.key))), perRow.length ? _jsx("td", {}) : null] }) })) : null] }) }), controls.footer ?? (total === undefined || total === 0 ? null : (_jsx("div", { className: "flex items-center justify-between gap-4 border-t px-5 py-4 text-sm text-muted-foreground", children: _jsx("span", { "data-showing": true, className: "tabular-nums", children: ctx.i18n.t('list.showing', { n: rows.length.toLocaleString(numbersIn(ctx.i18n.locale)), total: total.toLocaleString(numbersIn(ctx.i18n.locale)) }) }) })))] }));
}
/** One control per field type this version knows. Anything else says so and offers nothing. */
export function FieldControl({ field, value, onChange, ctx, disabled, idPrefix, onCreateOption, checked, lineErrors, onCreateLineOption }) {
    const id = fieldId(field.key, idPrefix);
    // A document's lines: the grid over the declared columns, never a skin's guess (lines.ts).
    if (field.type === 'lines') {
        return _jsx(LinesField, { field: field, value: value, onChange: onChange, ctx: ctx, id: id, ...(disabled !== undefined ? { disabled } : {}), ...(checked !== undefined ? { checked } : {}), ...(lineErrors ? { refused: lineErrors } : {}), ...(onCreateLineOption ? { onCreate: onCreateLineOption } : {}) });
    }
    // A choice from another table is a lookup, whatever skin the shell has for a select: which
    // control a field needs is decided here, once, and a table is not a closed list.
    if (field.type === 'select' && field.options_from?.path) {
        return (_jsx(Lookup, { id: id, value: value, disabled: disabled, placeholder: ctx.i18n.t('lookup.placeholder'), options: (field.options ?? []).map((o) => ({ value: o.value, label: resolveLabel(o.label, o.label_key, ctx.i18n), ...(o.group ? { detail: o.group } : {}) })), onChange: onChange, ...(field.options_from.create && onCreateOption ? { onCreate: onCreateOption } : {}), labels: { create: (text) => ctx.i18n.t('lookup.add', { text }), none: ctx.i18n.t('lookup.none'), clear: ctx.i18n.t('lookup.clear'), count: (n) => unitWords('results', n, ctx.i18n) } }));
    }
    // What a field declares about itself decides the control before any skin does: a unit, bounds, a
    // shape, a Hijri twin or a handful of choices are the design system's controls, drawn the same
    // way in every module, and a skin that knows none of it would quietly drop them.
    const declared = declaredControl({ field, value, onChange, ctx, disabled, id });
    if (declared !== undefined)
        return declared;
    const skin = field.type ? ctx.controls?.[field.type] : undefined;
    // A skin draws what it is handed, so it is handed choices already in the reader's language. The
    // shell's select wrote `label` as it stood, and every option declared with a catalogue key — an
    // account type, a journal type — showed its English fallback, or its raw value, in Arabic.
    const worded = field.options
        ? { ...field, options: field.options.map((o) => ({ ...o, label: resolveLabel(o.label, o.label_key, ctx.i18n) })) }
        : field;
    if (skin)
        return skin({ field: worded, value, onChange, disabled: disabled ?? false, id });
    switch (field.type) {
        case 'boolean':
            return _jsx(Checkbox, { id: id, checked: value === true, disabled: disabled, onChange: (e) => onChange(e.currentTarget.checked) });
        case 'integer':
        case 'number':
            return (_jsx(NumberInput, { id: id, value: typeof value === 'number' ? value : value === null || value === undefined || value === '' ? null : Number(value), integer: field.type === 'integer', disabled: disabled, onChange: onChange }));
        case 'select': {
            // A choice is identified by its position, not by how its value writes itself: "1" and 1 are
            // two choices, and naming them both "1" would send back whichever came first in the list.
            const options = field.options ?? [];
            const at = chosenIndex(options, value);
            return (_jsxs(Select, { id: id, value: at < 0 ? '' : String(at), disabled: disabled, onChange: (e) => onChange(e.currentTarget.value === '' ? null : options[Number(e.currentTarget.value)]?.value), children: [at < 0 || !field.required ? _jsx("option", { value: "" }) : null, options.map((o, i) => _jsx("option", { value: String(i), children: resolveLabel(o.label, o.label_key, ctx.i18n) }, i))] }));
        }
        case 'multiselect': {
            // Several of a list, and the answer is the list. Checkboxes rather than a multi-select box
            // because what is chosen has to be readable without opening anything: the question here is
            // usually "what does this already have", and a closed control answers it badly.
            const chosen = Array.isArray(value) ? value : [];
            return (_jsx("span", { role: "group", "aria-labelledby": id, "data-field": field.key, className: "grid gap-2", children: (field.options ?? []).map((option, i) => (_jsx(Checkbox, { disabled: disabled, label: resolveLabel(option.label, option.label_key, ctx.i18n), checked: chosen.some((v) => Object.is(v, option.value)), onChange: (e) => onChange(e.currentTarget.checked
                        ? [...chosen, option.value]
                        : chosen.filter((v) => !Object.is(v, option.value))) }, i))) }));
        }
        case 'date':
            return _jsx(DateInput, { id: id, locale: ctx.i18n.locale, value: typeof value === 'string' ? value : null, disabled: disabled, onChange: onChange });
        case 'time':
            return _jsx(TimeInput, { id: id, value: typeof value === 'string' ? value : null, disabled: disabled, onChange: onChange });
        case 'textarea':
            return _jsx(Textarea, { id: id, value: String(value ?? ''), disabled: disabled, onChange: (e) => onChange(e.currentTarget.value) });
        case 'text':
            return _jsx(Input, { id: id, type: "text", value: String(value ?? ''), disabled: disabled, onChange: (e) => onChange(e.currentTarget.value) });
        case 'secret':
            // Written, never read back: a password control the browser does not offer to fill from a
            // saved login, left to right, and never started from what the row holds (initialValues).
            return _jsx(Input, { id: id, type: "password", autoComplete: "new-password", dir: "ltr", spellCheck: false, "data-secret": "", value: typeof value === 'string' ? value : '', disabled: disabled, onChange: (e) => onChange(e.currentTarget.value) });
        case 'file': {
            // `store: media`: the file goes to media from here and the body carries its reference. A host
            // with no media to store in says so, rather than sending bytes the route is not written to read.
            const toMedia = field.store === 'media';
            if (toMedia && !ctx.storeFile)
                return _jsx("span", { className: "text-xs text-state-unavailable", "data-field": field.key, "data-file-store": "none", children: ctx.i18n.t('file.store.noMedia') });
            const one = (v) => toMedia
                ? (isMediaRef(v) ? v : null)
                : (v && typeof v === 'object' && !Array.isArray(v) && v.name ? v : null);
            const labels = { choose: ctx.i18n.t(field.multiple ? 'file.chooseMany' : 'file.choose'), change: ctx.i18n.t('file.change'), remove: ctx.i18n.t('file.remove'), add: ctx.i18n.t('file.add'),
                tooLarge: (limit) => ctx.i18n.t('file.tooLarge', { limit }), tooMany: (limit) => ctx.i18n.t('file.tooMany', { limit }),
                storing: ctx.i18n.t('file.storing'), notStored: (reason) => ctx.i18n.t('file.notStored', { reason }) };
            const shared = { id, accept: field.accept, disabled, locale: ctx.i18n.locale, labels,
                ...(field.capture ? { capture: field.capture } : {}),
                ...(toMedia && ctx.storeFile ? { store: ctx.storeFile } : {}) };
            if (field.multiple) {
                const held = (Array.isArray(value) ? value : []).map(one).filter((v) => v !== null);
                return _jsx(FileInput, { ...shared, multiple: true, value: held, onChange: onChange, ...(typeof field.max_files === 'number' ? { maxFiles: field.max_files } : {}) });
            }
            return _jsx(FileInput, { ...shared, value: one(value), onChange: onChange });
        }
        case 'audio': {
            // Always stored in media: a recording is never base64 in a body. Without media it says so.
            if (!ctx.storeFile)
                return _jsx("span", { className: "text-xs text-state-unavailable", "data-field": field.key, "data-file-store": "none", children: ctx.i18n.t('file.store.noMedia') });
            return (_jsx(AudioInput, { id: id, disabled: disabled, value: isMediaRef(value) ? value : null, onChange: onChange, store: ctx.storeFile, ...(typeof field.max_seconds === 'number' ? { maxSeconds: field.max_seconds } : {}), labels: { record: ctx.i18n.t('audio.record'), stop: ctx.i18n.t('audio.stop'), remove: ctx.i18n.t('file.remove'),
                    recording: (elapsed) => ctx.i18n.t('audio.recording', { elapsed }), limit: (limit) => ctx.i18n.t('audio.limit', { limit }),
                    unsupported: ctx.i18n.t('audio.unsupported'), denied: ctx.i18n.t('audio.denied'), failed: ctx.i18n.t('audio.failed'),
                    storing: ctx.i18n.t('file.storing'), notStored: (reason) => ctx.i18n.t('file.notStored', { reason }) } }));
        }
        default:
            // Guessing a control here would change what the person is able to express, and they would
            // have no way to know the value they saved is not the value they meant.
            return _jsx("span", { className: "text-xs text-state-unavailable", "data-field": field.key, children: ctx.i18n.t('field.unsupported') });
    }
}
/** Most choices a closed list may have and still be drawn as buttons rather than a list. */
export const SEGMENTED_MAX = 4;
/** Longest option, in characters, a choice may have and still be drawn as buttons. */
export const SEGMENTED_LABEL_MAX = 22;
/**
 * Whether a field's choices are drawn as buttons: a closed list of two to four that always has an
 * answer — required, or starting from a declared default. An optional choice with no default stays
 * a list, because buttons have no way back to "not given".
 */
export function segmented(field) {
    const n = field.options?.length ?? 0;
    return field.type === 'select' && !field.options_from?.path && n >= 2 && n <= SEGMENTED_MAX
        && (field.required === true || (field.value !== undefined && field.value !== null));
}
function declaredControl({ field, value, onChange, ctx, disabled, id }) {
    if (segmented(field)) {
        const options = field.options ?? [];
        const labels = options.map((o) => resolveLabel(o.label, o.label_key, ctx.i18n));
        // Buttons are for short answers compared at a glance. A choice whose words run long reads
        // better as a list, where each option has the whole width — decided on the words the reader
        // sees, in their language.
        if (labels.every((l) => l.length <= SEGMENTED_LABEL_MAX)) {
            const at = chosenIndex(options, value);
            return (_jsx(Segmented, { id: id, "aria-labelledby": `${id}-label`, disabled: disabled ?? false, value: at < 0 ? null : String(at), options: labels.map((label, i) => ({ value: String(i), label })), onChange: (i) => onChange(options[Number(i)]?.value) }));
        }
    }
    if ((field.type === 'number' || field.type === 'integer') && (field.unit || field.decimals !== undefined || field.min !== undefined || field.max !== undefined)) {
        return (_jsx(NumberInput, { id: id, disabled: disabled, integer: field.type === 'integer', value: typeof value === 'number' ? value : value === null || value === undefined || value === '' ? null : Number(value), ...(field.decimals !== undefined ? { decimals: field.decimals } : field.unit === 'sar' ? { decimals: 2 } : {}), ...(field.min !== undefined ? { min: field.min } : {}), ...(field.max !== undefined ? { max: field.max } : {}), ...(field.unit ? { unit: ctx.i18n.t(`unit.${field.unit}`) } : {}), 
            // A declared field keeps what was typed; the form says the bounds (rangeProblem) rather than moving it.
            clamp: false, onChange: onChange }));
    }
    if ((field.type === 'text' || field.type === undefined) && field.format) {
        const rule = FIELD_FORMATS[field.format];
        return (_jsx(Input, { id: id, type: field.format === 'email' ? 'email' : field.format === 'phone' ? 'tel' : 'text', dir: "ltr", disabled: disabled, value: String(value ?? ''), onChange: (e) => onChange(field.format === 'email' || field.format === 'code' || field.format === 'ltr' ? e.currentTarget.value : westernDigits(e.currentTarget.value)), spellCheck: false, autoComplete: "off", ...(rule?.inputMode ? { inputMode: rule.inputMode } : {}), ...(rule?.maxLength ? { maxLength: rule.maxLength } : {}), ...(rule?.placeholder ? { placeholder: rule.placeholder } : {}), className: "font-mono tabular-nums tracking-wide" }));
    }
    if (field.type === 'multiselect' && (field.options ?? []).some((o) => o.row && o.column)) {
        return _jsx(ChoiceGrid, { id: id, field: field, value: value, onChange: onChange, ctx: ctx, disabled: disabled ?? false });
    }
    if (field.type === 'multiselect' && (field.options?.length ?? 0) > CHIPS_AFTER) {
        return _jsx(Chips, { id: id, field: field, value: value, onChange: onChange, ctx: ctx, disabled: disabled ?? false });
    }
    if (field.type === 'keyvalue') {
        return _jsx(KeyValue, { id: id, value: value, onChange: onChange, ctx: ctx, disabled: disabled ?? false });
    }
    if (field.type === 'date' && field.hijri) {
        return _jsx(DateInput, { id: id, hijri: true, locale: ctx.i18n.locale, value: typeof value === 'string' ? value : null, disabled: disabled, onChange: onChange });
    }
    return undefined;
}
/** The grid's columns, in the order they are drawn; a column no option names is left out. */
export const GRID_ACTIONS = ['view', 'create', 'update', 'delete', 'manage'];
/**
 * A multiselect whose choices name a `row` and a `column` — permissions by resource and action —
 * drawn as the design system's check grid, a section per `group` (module). What was ticked or
 * cleared since the form opened is marked, and each section can be set whole. The answer is still
 * the list of chosen values; the grid is how it is read and changed.
 */
function ChoiceGrid({ id, field, value, onChange, ctx, disabled }) {
    const chosen = Array.isArray(value) ? value.map(String) : [];
    const [initial] = useState(() => new Set(chosen));
    const options = field.options ?? [];
    const columns = GRID_ACTIONS.filter((a) => options.some((o) => o.row && o.column === a));
    const set = (values, on) => {
        const next = new Set(chosen);
        for (const v of values) {
            if (on)
                next.add(v);
            else
                next.delete(v);
        }
        onChange(options.map((o) => o.value).filter((v) => next.has(String(v))));
    };
    const cellOf = (o) => {
        const v = String(o.value);
        const checked = chosen.includes(v);
        const change = checked && !initial.has(v) ? 'added' : !checked && initial.has(v) ? 'removed' : undefined;
        return { value: v, label: resolveLabel(o.label, o.label_key, ctx.i18n), checked, disabled, ...(change ? { change } : {}) };
    };
    const groups = [];
    for (const o of options) {
        const name = o.group ?? '';
        const found = groups.find((g) => g.name === name);
        if (found)
            found.items.push(o);
        else
            groups.push({ name, items: [o] });
    }
    const sections = groups.map((g) => {
        const inGrid = g.items.filter((o) => o.row && o.column && columns.includes(o.column));
        const others = g.items.filter((o) => !inGrid.includes(o));
        const rows = [...new Set(inGrid.map((o) => o.row))].map((row) => ({
            key: row,
            label: _jsx("bdi", { dir: "ltr", className: "font-mono text-[13px]", children: shortResource(row, inGrid[0]?.value) }),
            cells: Object.fromEntries(inGrid.filter((o) => o.row === row).map((o) => [o.column, cellOf(o)])),
        }));
        const values = g.items.map((o) => String(o.value));
        const held = values.filter((v) => chosen.includes(v)).length;
        return {
            key: g.name || '-', title: g.name || ctx.i18n.t('grid.others'), rows,
            meta: ctx.i18n.t('grid.count', { held, of: values.length }),
            ...(disabled ? {} : { aside: (_jsxs(_Fragment, { children: [_jsx(Button, { size: "sm", variant: "quiet", "data-grid-all": g.name, disabled: held === values.length, onClick: () => set(values, true), children: ctx.i18n.t('grid.all') }), _jsx(Button, { size: "sm", variant: "quiet", "data-grid-none": g.name, disabled: held === 0, onClick: () => set(values, false), children: ctx.i18n.t('grid.none') })] })) }),
            ...(others.length ? { others: others.map(cellOf), othersTitle: ctx.i18n.t('grid.others') } : {}),
        };
    });
    return (_jsx(CheckGrid, { id: id, role: "group", "aria-labelledby": `${id}-label`, "data-field": field.key, columns: columns.map((c) => ({ key: c, label: ctx.i18n.t(`grid.action.${c}`) })), sections: sections, onCheck: (v, on) => set([v], on), labels: { rows: ctx.i18n.t('grid.rows'), added: ctx.i18n.t('grid.added'), removed: ctx.i18n.t('grid.removed'), granted: ctx.i18n.t('grid.granted') } }));
}
/** `identity.user` under a section already headed Identity is `user`. Nothing else is changed. */
function shortResource(resource, sampleKey) {
    const module = String(sampleKey ?? '').split('.')[0];
    return module && resource.startsWith(`${module}.`) ? resource.slice(module.length + 1) : resource;
}
/** More choices than this, and a multiselect is a search that adds chips rather than a wall of boxes. */
export const CHIPS_AFTER = 8;
/**
 * Several of a long list: what is chosen as chips that can each be removed, and a search to add
 * another. A wall of thirty checkboxes answers "what is chosen" only by reading all thirty.
 */
function Chips({ id, field, value, onChange, ctx, disabled }) {
    const options = (field.options ?? []).map((o) => ({ value: o.value, label: resolveLabel(o.label, o.label_key, ctx.i18n) }));
    const chosen = Array.isArray(value) ? value : [];
    // The search starts empty again after each choice.
    const [round, setRound] = useState(0);
    return (_jsxs("span", { className: "grid gap-2", "data-chips": field.key, children: [chosen.length ? (_jsx("span", { className: "flex flex-wrap gap-1.5", children: chosen.map((v) => {
                    const label = options.find((o) => Object.is(o.value, v))?.label ?? String(v);
                    return (_jsxs("span", { "data-chip": String(v), className: "inline-flex items-center gap-1 rounded-full border bg-muted py-0.5 ps-3 pe-1 text-[13px]", children: [_jsx("span", { dir: "auto", children: label }), _jsx(IconButton, { size: "sm", label: ctx.i18n.t('chips.remove', { label }), disabled: disabled, onClick: () => onChange(chosen.filter((x) => !Object.is(x, v))), children: "\u00D7" })] }, String(v)));
                }) })) : null, _jsx(Lookup, { id: id, value: null, disabled: disabled, placeholder: ctx.i18n.t('chips.add'), options: options.filter((o) => !chosen.some((c) => Object.is(c, o.value))), onChange: (v) => { if (v !== null && v !== undefined) {
                    onChange([...chosen, v]);
                    setRound((r) => r + 1);
                } }, labels: { none: ctx.i18n.t('lookup.none'), clear: ctx.i18n.t('lookup.clear'), count: (n) => unitWords('results', n, ctx.i18n) } }, round)] }));
}
/**
 * A few "name = value" rows — the conditions a rule matches on. Sent as an object; an empty name
 * is a row still being written and is not sent.
 */
function KeyValue({ id, value, onChange, ctx, disabled }) {
    const given = value && typeof value === 'object' && !Array.isArray(value) ? Object.entries(value).map(([k, v]) => [k, String(v ?? '')]) : [];
    const [rows, setRows] = useState(() => (given.length ? given : [['', '']]));
    const emit = (next) => {
        setRows(next);
        const out = {};
        for (const [k, v] of next)
            if (k.trim())
                out[k.trim()] = v.trim();
        onChange(Object.keys(out).length ? out : null);
    };
    return (_jsxs("span", { id: id, role: "group", className: "grid gap-2", "data-keyvalue": "", children: [rows.map(([k, v], i) => (_jsxs("span", { className: "flex items-center gap-2", children: [_jsx(Input, { dir: "ltr", className: "font-mono", "aria-label": ctx.i18n.t('keyvalue.name'), placeholder: ctx.i18n.t('keyvalue.name'), value: k, disabled: disabled, onChange: (e) => emit(rows.map((r, j) => (j === i ? [e.currentTarget.value, r[1]] : r))) }), _jsx("span", { "aria-hidden": true, className: "text-muted-foreground", children: "=" }), _jsx(Input, { "aria-label": ctx.i18n.t('keyvalue.value'), placeholder: ctx.i18n.t('keyvalue.value'), value: v, disabled: disabled, onChange: (e) => emit(rows.map((r, j) => (j === i ? [r[0], e.currentTarget.value] : r))) }), _jsx(IconButton, { size: "sm", label: ctx.i18n.t('keyvalue.remove'), disabled: disabled || (rows.length === 1 && !k && !v), onClick: () => emit(rows.length === 1 ? [['', '']] : rows.filter((_, j) => j !== i)), children: "\u00D7" })] }, i))), _jsx("span", { children: _jsx(Button, { variant: "quiet", size: "sm", disabled: disabled, onClick: () => setRows([...rows, ['', '']]), children: ctx.i18n.t('keyvalue.add') }) })] }));
}
/** The id a control and its label agree on. Exported so a screen can point htmlFor at the same one. */
export function fieldId(key, prefix) {
    return prefix ? `field-${prefix}-${key}` : `field-${key}`;
}
/** Which option holds this value, by identity. -1 when none does, including "nothing chosen". */
export function chosenIndex(options, value) {
    return options.findIndex((o) => Object.is(o.value, value));
}
/**
 * Wraps a screen: the permission first, then what the manifest asks for, then the data's state.
 * The order matters — a screen the viewer may not open should say so rather than fetch and fail.
 */
export function Screen({ manifest, result, ctx, children }) {
    if (manifest.permission && !ctx.holds(manifest.permission)) {
        return _jsx(StateNotice, { state: "forbidden", i18n: ctx.i18n, detail: manifest.permission });
    }
    // An unknown action.component is a missing dialog editor, not a refused list.
    const problems = inspect(manifest, (n) => ctx.components.has(n))
        .filter((p) => p.code !== 'UNKNOWN_COMPONENT');
    if (problems.length)
        return _jsx(ManifestRefusal, { problems: problems, i18n: ctx.i18n });
    if (result.state === 'loading')
        return _jsx(StateNotice, { state: "loading", i18n: ctx.i18n });
    if (result.state !== 'ok')
        return _jsx(StateNotice, { state: result.state, detail: result.detail, said: result, i18n: ctx.i18n });
    return _jsx(_Fragment, { children: children(result.data) });
}
/**
 * An action this version cannot carry out. Shown, disabled, and saying why.
 *
 * Not a refusal of the whole screen: an action is one control on it, and refusing People because
 * `invite` has nowhere to POST would take away the list as well. Not hidden either — a person who
 * has been given permission to invite should see that the button exists and that it is not ready,
 * rather than wonder whether they were meant to have it.
 */
function ActionButton({ action, ctx, onRun, describedBy }) {
    const ready = Boolean(onRun) && runnable(action);
    // Held back by its pre-open condition: disabled like one this version cannot run, but it is a
    // fact about here and now, said beside it (HeldReason) rather than as «not yet».
    const held = heldBack(action);
    // Three weights, and the manifest chooses between them: the one thing this screen is for, the
    // one that takes something away, and everything else.
    const variant = action.primary ? 'primary' : action.destructive ? 'destructive' : 'secondary';
    // Its shortcut (#93): the one the manifest names, or `N` for the one thing this screen is for.
    const keys = ready ? ariaKeyshortcuts(action.hotkey ?? (action.primary ? PRIMARY_HOTKEY : null)) : null;
    const title = held ? heldWords(action, ctx) : ctx.i18n.t('action.notYet');
    return (_jsx(Button, { size: "lg", variant: variant, disabled: !ready, "data-action": action.key, ...(keys ? { 'aria-keyshortcuts': keys } : {}), ...(action.primary ? { 'data-primary': '' } : {}), ...(action.destructive ? { 'data-destructive': '' } : {}), ...(held ? { 'data-action-held': action.available.held } : {}), ...(describedBy ? { 'aria-describedby': describedBy } : {}), ...(ready ? { onClick: () => onRun?.(action) } : { title }), icon: ready || held ? _jsx(Mark, { name: action.icon, ctx: ctx, className: "size-4" }) : _jsx(StateMark, { state: "planned", className: "shrink-0" }), children: resolveLabel(action.label, action.label_key, ctx.i18n) }));
}
/** Why a held action cannot be run now: its declared reason once the route said no; while it is asked, that it is being checked. */
export function heldWords(action, ctx) {
    const spec = action.available;
    if (!spec || spec.held === 'pending')
        return ctx.i18n.t('action.held.pending');
    return resolveLabel(spec.reason, spec.reason_key, ctx.i18n) || ctx.i18n.t('action.held.no');
}
/**
 * Where a held action is put right (`available.link`): a screen of the action's own module, as the
 * shell addresses it (`#module/screen`) — only for a viewer who holds the link's permission, since a
 * link that ends in a refusal is worse than none.
 */
export function heldLink(action, module, ctx) {
    const link = action.available?.link;
    if (!link || action.available?.held !== 'no' || !module)
        return null;
    if (link.permission && !ctx.holds(link.permission))
        return null;
    return { href: `#${module}/${link.screen}`, label: resolveLabel(link.label, link.label_key, ctx.i18n) };
}
/**
 * The line beside a held action that says why it cannot be run here, and the way to put it right —
 * in words a touch screen can read too, not only in the disabled button's title. Nothing while the
 * route is still asked: «checking» beside a button for a second is noise.
 */
function HeldReason({ action, module, ctx, id }) {
    if (action.available?.held !== 'no')
        return null;
    const link = heldLink(action, module, ctx);
    return (_jsxs("span", { id: id, "data-action-held-reason": action.key, className: "max-w-[28rem] text-[13px] leading-snug text-muted-foreground", children: [heldWords(action, ctx), link ? _jsxs(_Fragment, { children: [" ", _jsx(InlineLink, { href: link.href, "data-action-held-link": action.key, children: link.label })] }) : null] }));
}
/**
 * A screen's own actions — the ones about the list rather than about a row.
 *
 * Exported because they belong beside the title, and the title belongs to whoever draws the page:
 * the shell for a declared screen, a unit for one it ships itself. Drawn here so both get the same
 * three weights and the same refusals.
 *
 * The primary one is drawn last, which in either direction puts it at the end of the row — the
 * corner the eye leaves from, and the one the reference this was measured against uses.
 */
export function ScreenActions({ manifest, ctx, onRun }) {
    // While the catalogue an empty list waits on is known to be empty (resolveEmpty), the list's own
    // create is not offered beside the title either: it could only be refused for want of a type, and
    // the empty block already offers defining the catalogue instead.
    const waiting = manifest.empty?.catalogue?.none === true ? manifest.empty.action : undefined;
    const allowed = (manifest.actions ?? [])
        .filter((a) => !isRowAction(a) && !a.lookup_only && a.key !== waiting)
        .filter((a) => !a.permission || ctx.holds(a.permission));
    if (!allowed.length)
        return null;
    const ordered = [...allowed.filter((a) => !a.primary), ...allowed.filter((a) => a.primary)];
    // A held action's reason is drawn before it, so the words lead to the button they explain.
    const module = manifest.screen.includes('.') ? manifest.screen.slice(0, manifest.screen.indexOf('.')) : '';
    return (_jsx("div", { className: "flex flex-wrap items-center gap-3", "data-screen-actions": true, children: ordered.map((a) => {
            const why = a.available?.held === 'no' ? `held-${manifest.screen}-${a.key}` : undefined;
            return (_jsxs(Fragment, { children: [why ? _jsx(HeldReason, { action: a, module: module, ctx: ctx, id: why }) : null, _jsx(ActionButton, { action: a, ctx: ctx, ...(onRun ? { onRun } : {}), ...(why ? { describedBy: why } : {}) })] }, a.key));
        }) }));
}
/**
 * A registered unit component. Create and edit live here — on an editor page or in ActionDialog —
 * not under the list. The same panel tokens every form wears. A row action hands the component the
 * row it is about, as a declared form is seeded from it: a component opened on one request has to
 * know which.
 */
export function ActionEditor({ action, ctx, sdk, row, onSaved, params }) {
    const name = action.component;
    if (!name)
        return null;
    const Comp = ctx.components.get(name);
    if (!Comp) {
        return _jsx(StateNotice, { state: ctx.componentsLoading ? 'loading' : 'unavailable', i18n: ctx.i18n, ...(ctx.componentsLoading ? {} : { detail: name }) });
    }
    return (_jsx("div", { "data-action-editor": action.key, "data-slot-component": name, className: SLOT_PANEL, children: _jsx(Comp, { sdk: sdk, onSaved: onSaved, params: params ?? {}, ...(row ? { row } : {}) }) }));
}
/** `{column}` in a part's words, each written as its column writes it — or `{column:month}` and the other typed words of a template. */
export function fillWords(text, row, columns, ctx) {
    if (!row)
        return text.replace(/\{[A-Za-z_][A-Za-z0-9_]*(?::[a-z]+)?\}/g, '—');
    return text.replace(/\{([A-Za-z_][A-Za-z0-9_]*)(:[a-z]+)?\}/g, (whole, key, as) => {
        if (!(key in row))
            return whole;
        if (as)
            return templateWords(whole, row, ctx.i18n);
        const column = columns.find((c) => c.key === key) ?? { key, label: key };
        return formatCell(readersValue(row[key], column, row, ctx.i18n), column, ctx.i18n);
    });
}
/**
 * A figure in its column's words: the column's `template` when it declares one — «{occupancy}%»,
 * «{available} of {cars}», each `{column}` written by its own column — else the value as its column
 * writes it. What a table cell does with a template, for the places that draw a figure as text: a
 * card's figures, a hub's tiles and bars, a card's notice.
 */
export function figureWords(value, column, row, columns, ctx) {
    if (row && (column.template || column.template_key))
        return fillWords(resolveLabel(column.template, column.template_key, ctx.i18n), row, columns, ctx);
    return formatCell(value, column, ctx.i18n);
}
/** Whether every value a part's words name is there — a line that would read «— عن —» is not drawn at all. */
export function wordsHold(text, row) {
    if (!row)
        return false;
    return [...text.matchAll(/\{([A-Za-z_][A-Za-z0-9_]*)(?::[a-z]+)?\}/g)].every((m) => row[m[1]] !== null && row[m[1]] !== undefined && row[m[1]] !== '');
}
//# sourceMappingURL=render.js.map